import React, { useState, useEffect, useRef } from 'react';
import { SchoolSettings } from '../types';
import { Spinner } from './common/UIComponents';
import { useLanguage } from '../contexts/LanguageContext';
import { ThemeToggle } from './common/ThemeToggle';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, serverTimestamp, updateDoc, doc, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CinematicHeroCarousel } from './CinematicHeroCarousel';
import { brand } from '../whitelabel';
import { useSchool, DEFAULT_SCHOOL_ID } from '../contexts/SchoolContext';
import { SchoolPartnershipModal } from './SchoolPartnershipModal';
import TryDemo from './demo/TryDemo';
import { discountSummary, usePricing } from '../services/billingClient';
import { PLANS, basePrice, perPrice } from '../services/pricingCore';

interface LandingPageProps {
  onLogin: (email: string, password: string) => void;
  schoolSettings: SchoolSettings;
  loading?: boolean;
  schools?: Array<{ id: string; name: string }>;
}

// ─── Partner school type ──────────────────────────────────────────────────────
interface PartnerSchool {
  id: string;
  name: string;
  short: string;
  location: string;
  type: string;
  levels: string[];
  description: string;
  email: string;
  phone: string;
  color: string;
  emoji: string;
  admissionsOpen: boolean;
  fee: string;
  deadline: string;
}

// ─── Enriched static detail for known schools (supplements Firestore data) ───
const SCHOOL_DETAILS: Record<string, Partial<PartnerSchool>> = {
  'kca-main': {
    short: 'KCA',
    levels: ['Pre-KG', 'KG 1', 'KG 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4'],
    description: 'A Cambridge-curriculum primary school in Kaduna offering Pre-KG through Grade 4. Known for strong academic foundations, personalised learning, and a nurturing environment for young learners.',
    color: '#1B3870',
    emoji: '🏫',
    fee: '₦5,000',
    deadline: 'July 31, 2025',
  },
};

// ─── Enrichment for CCIET — matched by any Firestore doc ID containing 'cciet' ─
const CCIET_DETAIL: Partial<PartnerSchool> = {
  short: 'CCIET',
  location: 'Canada 🇨🇦',
  type: 'Registered Training Institution',
  levels: ['Capacity Building', 'Professional Development', 'Corporate Training', 'Skills Certification'],
  description: 'A registered Canadian training institution specialising in intrinsic education and capacity building. CCIET empowers individuals and organisations through evidence-based learning programmes designed for lasting, values-driven growth.',
  color: '#C8102E',
  emoji: '🍁',
};

const ANNOUNCEMENTS = [
  '🐉 Historic MOU signed! MEF & TANG Chinese Education (Beijing) partner for Chinese language education, vocational skills training & joint certification in Nigeria',
  '🌍 MEF EduSuite goes international — CCIET (Canada) and TANG Chinese Education (China) are now official MEF partners',
  '📢 MEF EduSuite is now live — Institutions across Nigeria, Canada and China can register to join our network',
  '🏆 New feature: AI-powered CBT examination system now available to all partner institutions',
  '🚀 Join 1,000+ learners already growing smarter on the MEF EduSuite platform',
];

const NEWS = [
  {
    id: 1,
    category: 'Academic',
    date: 'Jun 3, 2025',
    title: 'MEF Students Excel in West African Examinations',
    excerpt: 'An unprecedented 94% of MEF candidates achieved A1–C6 across all core subjects in the 2024 WAEC examinations, placing us among the top schools in Kaduna State.',
    emoji: '🏅',
    color: '#10b981',
  },
  {
    id: 2,
    category: 'Partnership',
    date: 'Sep 9, 2025',
    title: 'MEF Signs Historic 5-Year MOU with TANG Chinese Education, Beijing',
    excerpt: 'The Muyikaratu Educational Foundation has signed a landmark Memorandum of Understanding with TANG Chinese Education & Technology Ltd. (Beijing, China) — establishing a 5-year framework for Chinese language education, vocational skills training (AI, engineering, hospitality, renewable energy), joint curriculum development, and industry-academia collaboration to transform the employability of Nigerian youth.',
    emoji: '🐉',
    color: '#C41E3A',
  },
  {
    id: 3,
    category: 'International',
    date: 'Jul 4, 2026',
    title: 'MEF EduSuite Welcomes CCIET Canada as International Partner',
    excerpt: 'The Canadian Center for Intrinsic Education and Training (CCIET), a registered Canadian training institution, has joined MEF EduSuite — marking another major milestone in MEF\'s growing global network spanning Nigeria, China, and Canada.',
    emoji: '🍁',
    color: '#C8102E',
  },
  {
    id: 4,
    category: 'Sports',
    date: 'Jul 4, 2026',
    title: 'MEF Sponsors Queens Planet FC — Developing Nigeria\'s Next Global Football Stars',
    excerpt: 'MEF is the official sponsor of Queens Planet FC, a Nigerian-based football club committed to discovering, developing, and promoting young talents into global football opportunities. Football is a world-class skill, and MEF is proud to invest in the next generation of Nigerian stars on the global stage.',
    emoji: '⚽',
    color: '#f59e0b',
  },
];

const STUDENT_OF_WEEK = {
  name: 'Umar Dalhat Hafsat',
  class: 'Rauda 2',
  score: 98.4,
  subject: 'Hisab & Al-Qur\'an',
  achievement: 'Perfect 100% scores in Al-Qur\'an & Hisab with a 98.4% Term 2 average',
  juzCompleted: 2,
  streak: 47,
  avatar: '👩‍🎓',
  badge: '🏆',
};

const TEACHER_OF_WEEK = {
  name: 'Khadija Usman Musa',
  subject: 'Islamic Studies & Tahfiz',
  department: 'Islamiyya, Tahfiz Section',
  achievement: 'Outstanding 2nd Term results with 100% student pass rate in early years Tahfiz',
  studentsHelped: 10,
  rating: 4.97,
  avatar: '👩‍🏫',
  badge: '⭐',
};


const TERM_INFO = {
  term: 'Second Term',
  session: '2025/2026',
  weekNumber: 12,
  totalWeeks: 13,
  examStart: 'April 6, 2026',
  examEnd: 'April 17, 2026',
  resumption: 'May 4, 2026',
};

const STATS = [
  { label: 'Active Learners', value: '1,200+', icon: '🎓' },
  { label: 'Partner Schools', value: '3+', icon: '🏫' },
  { label: 'Qualified Teachers', value: '80+', icon: '👨‍🏫' },
  { label: 'Founded', value: '2022', icon: '🏛️' },
];

// ─── Islamic star SVG pattern ────────────────────────────────────────────────
const StarPattern = ({ opacity = 0.06 }: { opacity?: number }) => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity }}>
    <defs>
      <pattern id="star-pat" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <polygon points="30,4 36,22 54,22 40,34 46,52 30,42 14,52 20,34 6,22 24,22" fill="none" stroke="currentColor" strokeWidth="0.8" />
        <circle cx="30" cy="30" r="3" fill="none" stroke="currentColor" strokeWidth="0.8" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#star-pat)" />
  </svg>
);


// ─── Login Modal ──────────────────────────────────────────────────────────────
const LoginModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => void;
}> = ({ open, onClose, onLogin }) => {
  const { t, language, setLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    try {
      await onLogin(email, password);
    } catch (err: any) {
      setLoginError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Do NOT set body overflow hidden — it doesn't prevent scroll on iOS Safari
  // and can freeze touch events inside fixed modals.

  if (!open) return null;

  return (
    // Outer: full-screen scrollable container so the form stays accessible when
    // the iOS virtual keyboard pushes content up.
    <div className="fixed inset-0 z-[200] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
      {/* Backdrop as its own fixed layer — keeping backdrop-filter off the form's
          ancestor avoids a WebKit bug where inputs inside backdrop-filtered
          containers become untappable on iOS Safari. */}
      <div
        className="fixed inset-0"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } as React.CSSProperties}
        onClick={onClose}
      />

      <div className="relative z-10 min-h-full flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-md animate-scale-in rounded-3xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(16,185,129,0.15)' }}
        >
          {/* decorative top bar */}
          <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#10b981,#06b6d4,#8b5cf6)' }} />

          {/* Islamic pattern bg */}
          <StarPattern opacity={0.04} />

          {/* close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all z-20"
          >
            ✕
          </button>

          <div className="p-8 relative z-10">
            {/* Logo area */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-3xl" style={{ background: 'linear-gradient(135deg,#10b981,#06b6d4)', boxShadow: '0 8px 24px rgba(16,185,129,0.35)' }}>
                🏫
              </div>
              <h2 className="text-2xl font-black text-white mb-1">Welcome Back</h2>
              <p className="text-slate-400 text-sm">Sign in to MEF EduSuite</p>

              {/* Language pills */}
              <div className="flex items-center justify-center gap-2 mt-4">
                {[['en', '🇬🇧', 'EN'], ['ar', '🇸🇦', 'AR'], ['ha', '🇳🇬', 'HA']].map(([code, flag, lbl]) => (
                  <button
                    key={code}
                    onClick={() => setLanguage(code as any)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${language === code ? 'bg-emerald-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}
                  >
                    {flag} {lbl}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-300 mb-2">{t('auth.email') || 'Email Address'}</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value.trim())}
                  required
                  placeholder="name@kca.edu.ng"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                  spellCheck={false}
                  inputMode="email"
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-slate-300">{t('auth.password') || 'Password'}</label>
                  <button type="button" className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold transition-colors">
                    {t('auth.forgotPassword') || 'Forgot password?'}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="current-password"
                    spellCheck={false}
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all pr-12"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-lg"
                  >
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-red-300 bg-red-500/15 border border-red-500/30">
                  <span className="shrink-0">⚠️</span>
                  <span>{loginError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl font-bold text-white text-base transition-all duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 8px 24px rgba(16,185,129,0.35)' }}
              >
                {isSubmitting ? (
                  <><Spinner className="w-5 h-5 text-white" /><span>Signing in…</span></>
                ) : (
                  <span>{t('auth.signIn') || 'Sign In to Portal'}</span>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-slate-500 mt-6">
              For login credentials, contact the school administration office.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Application constants ────────────────────────────────────────────────────
const APPLY_CLASSES = ['Hadana', 'Rauda 1', 'Rauda 2', 'Pre-KG', 'KG 1', 'KG 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5'];

// Available aptitude test slots: next 60 days, weekdays only
function getTestSlots(): string[] {
  const slots: string[] = [];
  const today = new Date();
  for (let d = 7; d <= 60 && slots.length < 20; d++) {
    const date = new Date(today);
    date.setDate(today.getDate() + d);
    const day = date.getDay();
    if (day !== 0 && day !== 6) { // Mon–Fri only
      slots.push(date.toISOString().split('T')[0]);
    }
  }
  return slots;
}

const SCHOOL_BANK_PUBLIC = {
  bank: 'First Bank of Nigeria',
  account: '3012345678',
  sortCode: '011',
};

type AppStep = 1 | 2 | 3 | 4;

interface ChildEntry {
  fullName: string;
  dob: string;
  gender: 'Male' | 'Female' | '';
  previousSchool: string;
  classAppliedFor: string;
}

interface AppForm {
  children: ChildEntry[];
  preferredTestDate: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  relationship: 'Father' | 'Mother' | 'Guardian' | 'Other' | '';
  paymentRef: string;
  payMode: 'bank' | 'online';
  selectedGateway: string;
}

interface PaymentMethodsMeta {
  enabledMethods: string[];
  bankDetails: { bankName: string; accountNumber: string; accountName: string } | null;
}

const GATEWAY_META: Record<string, { label: string; icon: string; color: string }> = {
  paystack: { label: 'Paystack', icon: '🇳🇬', color: 'rgba(0,195,247,0.15)' },
  flutterwave: { label: 'Flutterwave', icon: '🌍', color: 'rgba(245,166,35,0.15)' },
  stripe: { label: 'Stripe (USD)', icon: '💳', color: 'rgba(99,91,255,0.15)' },
};

const EMPTY_CHILD: ChildEntry = { fullName: '', dob: '', gender: '', previousSchool: '', classAppliedFor: '' };

const EMPTY_FORM: AppForm = {
  children: [{ ...EMPTY_CHILD }],
  preferredTestDate: '', parentName: '', parentEmail: '',
  parentPhone: '', relationship: '', paymentRef: '', payMode: 'bank', selectedGateway: '',
};

// ─── Application Modal ────────────────────────────────────────────────────────
const ApplicationModal: React.FC<{
  open: boolean;
  onClose: () => void;
  schoolSettings: SchoolSettings;
  school: PartnerSchool | null;
}> = ({ open, onClose, schoolSettings, school }) => {
  const [step, setStep] = useState<AppStep>(1);
  const [form, setForm] = useState<AppForm>({ ...EMPTY_FORM, children: [{ ...EMPTY_CHILD }] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const testSlots = useState(() => getTestSlots())[0];
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsMeta | null>(null);
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const classes = school?.levels?.length ? school.levels : APPLY_CLASSES;
  const feePerChild = school?.fee ? parseInt(school.fee.replace(/[^0-9]/g, ''), 10) || 5000 : 5000;
  const totalFee = feePerChild * form.children.length;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setForm({ ...EMPTY_FORM, children: [{ ...EMPTY_CHILD }] });
      setStep(1); setError('');
      setReceiptFile(null); setReceiptPreview('');
      const schoolId = (schoolSettings as any).schoolId || school?.id || 'kca-main';
      const fns = getFunctions();
      httpsCallable(fns, 'getSchoolPaymentMethods')({ schoolId })
        .then((r: any) => setPaymentMethods(r.data as PaymentMethodsMeta))
        .catch(() => setPaymentMethods({ enabledMethods: ['bank'], bankDetails: null }));
    } else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const setChild = (idx: number, key: keyof ChildEntry, val: string) =>
    setForm(f => { const ch = [...f.children]; ch[idx] = { ...ch[idx], [key]: val }; return { ...f, children: ch }; });

  const addChild = () => setForm(f => ({ ...f, children: [...f.children, { ...EMPTY_CHILD }] }));
  const removeChild = (idx: number) => setForm(f => ({ ...f, children: f.children.filter((_, i) => i !== idx) }));

  const childValid = (c: ChildEntry) => !!(c.fullName.trim() && c.dob && c.gender && c.classAppliedFor);

  const onlineGateways = (paymentMethods?.enabledMethods || []).filter(m => m !== 'bank');
  const hasOnlinePayment = onlineGateways.length > 0;

  const canNext = () => {
    if (step === 1) return form.children.length > 0 && form.children.every(childValid) && !!form.preferredTestDate;
    if (step === 2) return !!(form.parentName && form.parentEmail && form.parentPhone && form.relationship);
    if (step === 3) {
      if (form.payMode === 'online') return !!form.selectedGateway;
      return !!(form.paymentRef.trim() || receiptFile);
    }
    return true;
  };

  const handleReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = ev => setReceiptPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview('pdf');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const schoolId = (schoolSettings as any).schoolId || school?.id || 'kca-main';
      const baseDoc = {
        schoolId,
        schoolName: schoolSettings.schoolName || school?.name || 'KCA',
        createdAt: Date.now(),
        status: form.payMode === 'online' ? 'Pending Payment' : 'Pending',
        parentName: form.parentName,
        parentEmail: form.parentEmail.toLowerCase().trim(),
        parentPhone: form.parentPhone,
        relationship: form.relationship,
        children: form.children,
        childCount: form.children.length,
        childFullName: form.children[0].fullName,
        classAppliedFor: form.children[0].classAppliedFor,
        preferredTestDate: form.preferredTestDate,
        totalFee,
        feePerChild,
        paymentRef: form.paymentRef,
        paymentMethod: form.payMode === 'online' ? form.selectedGateway : 'bank_transfer',
        paymentVerified: false,
        adminNotes: '',
      };

      const docRef = await addDoc(collection(db, 'admission_applications'), baseDoc);

      // Upload receipt non-blocking
      if (receiptFile && storage) {
        try {
          const ext = receiptFile.name.split('.').pop() || 'jpg';
          const rRef = storageRef(storage, `admission_receipts/${docRef.id}/receipt.${ext}`);
          await uploadBytes(rRef, receiptFile, { contentType: receiptFile.type });
          const receiptUrl = await getDownloadURL(rRef);
          await updateDoc(doc(db, 'admission_applications', docRef.id), { receiptUrl });
        } catch (uploadErr) {
          console.warn('[admissionApply] Receipt upload failed (non-fatal):', uploadErr);
        }
      }

      // Online payment: redirect to gateway
      if (form.payMode === 'online' && form.selectedGateway) {
        try {
          const fns = getFunctions();
          const r = await httpsCallable(fns, 'initiateSchoolStudentPayment')({
            schoolId,
            invoiceId: docRef.id,
            studentEmail: form.parentEmail.toLowerCase().trim(),
            studentName: form.parentName,
            amount: totalFee,
            currency: form.selectedGateway === 'stripe' ? 'USD' : 'NGN',
            description: `Admission application — ${form.children.map(c => c.fullName).join(', ')}`,
            gateway: form.selectedGateway,
            callbackUrl: window.location.origin,
          }) as any;
          window.location.href = r.data.paymentUrl;
          return;
        } catch (payErr: any) {
          setError('Payment initiation failed: ' + (payErr.message || 'Please try bank transfer instead.'));
          setSubmitting(false);
          return;
        }
      }

      setStep(4);
    } catch (e: any) {
      setError('Submission failed. Please try again or contact the school office.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { onClose(); };

  if (!open) return null;

  const STEP_LABELS = ['Children', 'Parent Info', 'Payment'];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-xl max-h-[92vh] flex flex-col rounded-3xl overflow-hidden animate-scale-in"
        style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
        <div className="h-1 w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg,#8b5cf6,#06b6d4,#10b981)' }} />
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]" aria-hidden="true">
          <defs><pattern id="mp" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse"><polygon points="30,4 36,22 54,22 40,34 46,52 30,42 14,52 20,34 6,22 24,22" fill="none" stroke="white" strokeWidth="0.8" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#mp)" />
        </svg>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <h2 className="text-lg font-black text-white">Apply for Admission</h2>
            <p className="text-slate-400 text-sm">{schoolSettings.schoolName || school?.name || 'KCA'} · 2025/2026 Session</p>
          </div>
          <button onClick={reset} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all">✕</button>
        </div>

        {/* Step progress */}
        {step < 4 && (
          <div className="relative z-10 px-6 pt-4 flex-shrink-0">
            <div className="flex items-center gap-2 mb-3">
              {STEP_LABELS.map((lbl, i) => {
                const n = (i + 1) as AppStep; const active = step === n; const done = step > n;
                return (
                  <React.Fragment key={n}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${done ? 'bg-emerald-500 text-white' : active ? 'bg-violet-600 text-white' : 'bg-white/10 text-slate-500'}`}>
                        {done ? '✓' : n}
                      </div>
                      <span className={`text-[9px] font-bold whitespace-nowrap ${active ? 'text-violet-400' : done ? 'text-emerald-400' : 'text-slate-600'}`}>{lbl}</span>
                    </div>
                    {i < STEP_LABELS.length - 1 && <div className="flex-1 h-0.5 rounded-full mt-[-10px]" style={{ background: done ? '#10b981' : 'rgba(255,255,255,0.08)' }} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="relative z-10 flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* ── Step 1: Children ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-base font-black text-white">Children Applying</p>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-bold"
                  style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#a78bfa' }}>
                  {form.children.length} child{form.children.length !== 1 ? 'ren' : ''} · ₦{totalFee.toLocaleString()} total
                </div>
              </div>

              {form.children.map((child, idx) => (
                <div key={idx} className="rounded-2xl p-4 space-y-3"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-bold text-sm">Child {idx + 1}</span>
                    {form.children.length > 1 && (
                      <button onClick={() => removeChild(idx)} className="text-red-400 hover:text-red-300 text-xs font-semibold transition-all">✕ Remove</button>
                    )}
                  </div>

                  {/* Class selection */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-2">Class / Programme <span className="text-rose-400">*</span></label>
                    <div className="grid grid-cols-3 gap-2">
                      {classes.map(cls => (
                        <button key={cls} onClick={() => setChild(idx, 'classAppliedFor', cls)}
                          className="py-2 rounded-xl font-bold text-xs transition-all"
                          style={{
                            background: child.classAppliedFor === cls ? 'linear-gradient(135deg,#8b5cf6,#7c3aed)' : 'rgba(255,255,255,0.05)',
                            border: child.classAppliedFor === cls ? '1px solid rgba(139,92,246,0.6)' : '1px solid rgba(255,255,255,0.08)',
                            color: child.classAppliedFor === cls ? '#fff' : '#94a3b8',
                          }}>
                          {cls}
                        </button>
                      ))}
                    </div>
                  </div>

                  <FieldInput label="Full Name (Surname first)" value={child.fullName}
                    onChange={v => setChild(idx, 'fullName', v)} placeholder="e.g. Musa Fatima Al-Zahra" />

                  <div className="grid grid-cols-2 gap-3">
                    <FieldInput label="Date of Birth" type="date" value={child.dob}
                      onChange={v => setChild(idx, 'dob', v)} />
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-2">Gender <span className="text-rose-400">*</span></label>
                      <div className="flex gap-2">
                        {(['Male', 'Female'] as const).map(g => (
                          <button key={g} onClick={() => setChild(idx, 'gender', g)}
                            className="flex-1 py-2 rounded-xl font-bold text-xs transition-all"
                            style={{
                              background: child.gender === g ? 'rgba(6,182,212,0.2)' : 'rgba(255,255,255,0.05)',
                              border: child.gender === g ? '1px solid rgba(6,182,212,0.5)' : '1px solid rgba(255,255,255,0.08)',
                              color: child.gender === g ? '#06b6d4' : '#94a3b8',
                            }}>
                            {g === 'Male' ? '👦' : '👧'} {g}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <FieldInput label="Previous School (if any)" value={child.previousSchool}
                    onChange={v => setChild(idx, 'previousSchool', v)} placeholder="Leave blank if none" required={false} />
                </div>
              ))}

              <button onClick={addChild}
                className="w-full py-3 rounded-2xl font-bold text-sm transition-all hover:scale-[1.01]"
                style={{ background: 'rgba(16,185,129,0.08)', border: '1px dashed rgba(16,185,129,0.4)', color: '#10b981' }}>
                + Add Another Child
              </button>

              <div>
                <label className="block text-sm font-bold text-slate-300 mb-1">Preferred Aptitude Test Date <span className="text-rose-400">*</span></label>
                <p className="text-slate-500 text-xs mb-3">Final date confirmed by the school after payment verification.</p>
                <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                  {testSlots.map(slot => {
                    const d = new Date(slot);
                    const label = d.toLocaleDateString('en-NG', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <button key={slot} onClick={() => setForm(f => ({ ...f, preferredTestDate: slot }))}
                        className="py-2.5 px-3 rounded-xl text-xs font-bold transition-all"
                        style={{
                          background: form.preferredTestDate === slot ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                          border: form.preferredTestDate === slot ? '1px solid rgba(16,185,129,0.5)' : '1px solid rgba(255,255,255,0.08)',
                          color: form.preferredTestDate === slot ? '#10b981' : '#94a3b8',
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Parent Info ── */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-base font-black text-white">Parent / Guardian Information</p>
              <FieldInput label="Full Name" value={form.parentName}
                onChange={v => setForm(f => ({ ...f, parentName: v }))} placeholder="Parent's full name" />
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-2">Relationship <span className="text-rose-400">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {(['Father', 'Mother', 'Guardian', 'Other'] as const).map(r => (
                    <button key={r} onClick={() => setForm(f => ({ ...f, relationship: r }))}
                      className="py-2.5 rounded-xl font-bold text-sm transition-all"
                      style={{
                        background: form.relationship === r ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.05)',
                        border: form.relationship === r ? '1px solid rgba(245,158,11,0.5)' : '1px solid rgba(255,255,255,0.08)',
                        color: form.relationship === r ? '#f59e0b' : '#94a3b8',
                      }}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <FieldInput label="Email Address" type="email" value={form.parentEmail}
                onChange={v => setForm(f => ({ ...f, parentEmail: v }))} placeholder="parent@email.com" />
              <FieldInput label="Phone Number" type="tel" value={form.parentPhone}
                onChange={v => setForm(f => ({ ...f, parentPhone: v }))} placeholder="+234 800 000 0000" />
            </div>
          )}

          {/* ── Step 3: Payment ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-base font-black text-white">Application Fee Payment</p>

              {/* Fee breakdown */}
              <div className="rounded-2xl p-4 space-y-2"
                style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)' }}>
                <p className="text-slate-300 text-sm font-bold mb-3">Fee Breakdown</p>
                {form.children.map((c, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-400">{c.fullName || `Child ${i + 1}`} ({c.classAppliedFor})</span>
                    <span className="text-white font-semibold">₦{feePerChild.toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
                  <span className="text-white font-black">Total</span>
                  <span className="text-violet-400 font-black text-lg">₦{totalFee.toLocaleString()}</span>
                </div>
              </div>

              {/* Payment mode tabs — show only if online gateways are enabled */}
              {hasOnlinePayment && (
                <div className="flex gap-2">
                  {(['online', 'bank'] as const).map(mode => (
                    <button key={mode} onClick={() => setForm(f => ({ ...f, payMode: mode, selectedGateway: '' }))}
                      className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all"
                      style={{
                        background: form.payMode === mode ? (mode === 'online' ? 'rgba(99,91,255,0.2)' : 'rgba(16,185,129,0.15)') : 'rgba(255,255,255,0.05)',
                        border: form.payMode === mode ? (mode === 'online' ? '1px solid rgba(99,91,255,0.5)' : '1px solid rgba(16,185,129,0.4)') : '1px solid rgba(255,255,255,0.08)',
                        color: form.payMode === mode ? '#fff' : '#94a3b8',
                      }}>
                      {mode === 'online' ? '💳 Pay Online' : '🏦 Bank Transfer'}
                    </button>
                  ))}
                </div>
              )}

              {/* Online payment: gateway selection */}
              {form.payMode === 'online' && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Select Payment Gateway</p>
                  {onlineGateways.map(gw => {
                    const meta = GATEWAY_META[gw] || { label: gw, icon: '💳', color: 'rgba(255,255,255,0.08)' };
                    return (
                      <button key={gw} onClick={() => setForm(f => ({ ...f, selectedGateway: gw }))}
                        className="w-full flex items-center gap-3 p-4 rounded-xl transition-all text-left"
                        style={{
                          background: form.selectedGateway === gw ? meta.color : 'rgba(255,255,255,0.04)',
                          border: form.selectedGateway === gw ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.08)',
                        }}>
                        <span className="text-2xl">{meta.icon}</span>
                        <div className="flex-1">
                          <p className="text-white font-bold text-sm">{meta.label}</p>
                          <p className="text-slate-400 text-xs">{gw === 'stripe' ? 'International card (USD)' : gw === 'paystack' ? 'Card · Bank · USSD (NGN)' : 'Card · Mobile money (NGN)'}</p>
                        </div>
                        {form.selectedGateway === gw && <span className="text-emerald-400 font-black">✓</span>}
                      </button>
                    );
                  })}
                  <div className="rounded-xl p-3" style={{ background: 'rgba(99,91,255,0.08)', border: '1px solid rgba(99,91,255,0.2)' }}>
                    <p className="text-slate-400 text-xs">Your application will be saved and you will be redirected to complete payment securely. The admissions team will be notified automatically.</p>
                  </div>
                </div>
              )}

              {/* Bank transfer */}
              {form.payMode === 'bank' && (
                <div className="space-y-3">
                  <div className="rounded-2xl p-4 space-y-2"
                    style={{ background: 'linear-gradient(135deg,rgba(16,185,129,0.1),rgba(6,182,212,0.06))', border: '1px solid rgba(16,185,129,0.25)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">🏦</span>
                      <p className="text-white font-black text-sm">Pay to this account</p>
                    </div>
                    {[
                      { label: 'Bank', val: paymentMethods?.bankDetails?.bankName || (schoolSettings as any).bankName || SCHOOL_BANK_PUBLIC.bank },
                      { label: 'Account Number', val: paymentMethods?.bankDetails?.accountNumber || (schoolSettings as any).accountNumber || SCHOOL_BANK_PUBLIC.account },
                      { label: 'Account Name', val: paymentMethods?.bankDetails?.accountName || (schoolSettings as any).accountName || schoolSettings.schoolName || 'MEF EduSuite' },
                    ].map(row => (
                      <div key={row.label} className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <span className="text-slate-400 text-xs">{row.label}</span>
                        <span className="text-white font-bold text-xs select-all">{row.val}</span>
                      </div>
                    ))}
                    <p className="text-slate-500 text-xs pt-1">Use <strong className="text-slate-300">{form.children[0]?.fullName || "your child's name"}</strong> as narration.</p>
                  </div>

                  <FieldInput label="Transfer Reference / Narration (optional if uploading receipt)"
                    value={form.paymentRef} onChange={v => setForm(f => ({ ...f, paymentRef: v }))}
                    placeholder="e.g. TRF-2025-000123" required={false} />

                  {/* Receipt upload */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-2">Upload Payment Receipt <span className="text-slate-500 font-normal">(photo or PDF)</span></label>
                    <input ref={receiptInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptChange} />
                    {receiptPreview && receiptPreview !== 'pdf' ? (
                      <div className="relative mb-2">
                        <img src={receiptPreview} alt="Receipt" className="w-full max-h-32 object-cover rounded-xl" />
                        <button onClick={() => { setReceiptFile(null); setReceiptPreview(''); }}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white text-xs flex items-center justify-center hover:bg-red-500/80">✕</button>
                      </div>
                    ) : receiptPreview === 'pdf' ? (
                      <div className="flex items-center gap-2 mb-2 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <span>📄</span>
                        <span className="text-red-300 text-xs font-semibold truncate">{receiptFile?.name}</span>
                        <button onClick={() => { setReceiptFile(null); setReceiptPreview(''); }} className="ml-auto text-xs text-slate-400 hover:text-white">✕</button>
                      </div>
                    ) : null}
                    <button type="button" onClick={() => receiptInputRef.current?.click()}
                      className="w-full py-3 rounded-xl font-bold text-sm transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.2)', color: '#94a3b8' }}>
                      {receiptFile ? '↺ Change Receipt' : '📎 Upload Receipt'}
                    </button>
                  </div>

                  <div className="rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                    <p className="text-amber-400 text-xs font-bold">⚠️ Payment verified within 24 hours. You will be contacted to confirm your aptitude test date.</p>
                  </div>
                </div>
              )}

              {error && <p className="text-red-400 text-sm font-semibold">{error}</p>}
            </div>
          )}

          {/* ── Step 4: Success ── */}
          {step === 4 && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-black text-white mb-2">Application Submitted!</h3>
              <p className="text-slate-400 mb-1">Thank you, <strong className="text-white">{form.parentName}</strong>.</p>
              <p className="text-slate-400 mb-5">
                {form.children.length === 1
                  ? <>Application for <strong className="text-violet-400">{form.children[0].fullName}</strong> ({form.children[0].classAppliedFor}) received.</>
                  : <>{form.children.length} children's applications received successfully.</>}
              </p>
              <div className="rounded-2xl p-4 mb-5 text-left space-y-2"
                style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
                <p className="text-emerald-400 font-bold text-sm">What happens next?</p>
                <p className="text-slate-400 text-sm">1. Admissions team verifies your payment (₦{totalFee.toLocaleString()}) within 24 hours.</p>
                <p className="text-slate-400 text-sm">2. You'll receive a call/email confirming the aptitude test date.</p>
                <p className="text-slate-400 text-sm">3. Attend the test with your {form.children.length > 1 ? 'children' : 'child'} on the confirmed date.</p>
                <p className="text-slate-400 text-sm">4. Results within 3 working days of the test.</p>
              </div>
              <p className="text-slate-500 text-xs mb-5">Enquiries: {schoolSettings.schoolPhone || '+234 (0) 800 123 KCA'} · {schoolSettings.schoolEmail || 'admissions@kca.edu.ng'}</p>
              <button onClick={reset} className="px-6 py-3 rounded-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>Close</button>
            </div>
          )}
        </div>

        {/* Footer nav */}
        {step < 4 && (
          <div className="relative z-10 flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={() => step > 1 ? setStep(s => (s - 1) as AppStep) : reset()}
              className="px-5 py-2.5 rounded-xl font-bold text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-all">
              {step === 1 ? 'Cancel' : '← Back'}
            </button>
            {step < 3 ? (
              <button onClick={() => canNext() && setStep(s => (s + 1) as AppStep)} disabled={!canNext()}
                className="px-6 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)' }}>
                Continue →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting || !canNext()}
                className="px-6 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-40 flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                {submitting ? <><Spinner className="w-4 h-4" /> Submitting…</> : '🎓 Submit Application'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Small reusable field input
const FieldInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
}> = ({ label, value, onChange, placeholder, type = 'text', required = true }) => (
  <div>
    <label className="block text-xs font-bold text-slate-400 mb-1.5">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-all"
      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
    />
  </div>
);

// ─── Schools Discovery Modal ──────────────────────────────────────────────────
const SchoolsModal: React.FC<{ open: boolean; onClose: () => void; onApply: (school: PartnerSchool) => void; schools: PartnerSchool[]; loadingSchools: boolean }> = ({ open, onClose, onApply, schools, loadingSchools }) => {
  const [selected, setSelected] = useState<PartnerSchool | null>(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden animate-scale-in" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
        <div className="h-1 w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg,#1B3870,#3D9B3D,#E8B400)' }} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            {selected ? (
              <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-bold mb-1">
                ← Back to Schools
              </button>
            ) : null}
            <h2 className="text-lg font-black text-white">{selected ? selected.name : 'Schools on MEF EduSuite'}</h2>
            <p className="text-slate-400 text-sm">{selected ? selected.type : 'Choose a school to learn more or apply for admission'}</p>
          </div>
          <button onClick={() => { setSelected(null); onClose(); }} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {!selected ? (
            <div className="space-y-4">
              {loadingSchools ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                  <p className="text-slate-400 text-sm">Loading schools…</p>
                </div>
              ) : schools.length === 0 ? (
                <div className="text-center py-10 text-slate-500">No schools found.</div>
              ) : null}
              {schools.map(school => (
                <div key={school.id} onClick={() => setSelected(school)} className="group flex items-start gap-4 p-5 rounded-2xl cursor-pointer transition-all hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${school.color}33` }}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0" style={{ background: `${school.color}22`, border: `1px solid ${school.color}44` }}>
                    {school.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-black text-white">{school.name}</h3>
                      {school.admissionsOpen && (
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: '#3D9B3D22', color: '#4ABF4A', border: '1px solid #3D9B3D44' }}>OPEN</span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm mb-2">{school.type} · {school.location}</p>
                    <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">{school.description}</p>
                  </div>
                  <span className="text-slate-500 group-hover:text-white transition-colors mt-1">›</span>
                </div>
              ))}
              <div className="mt-6 p-4 rounded-2xl text-center" style={{ background: 'rgba(27,56,112,0.1)', border: '1px dashed rgba(27,56,112,0.3)' }}>
                <p className="text-slate-400 text-sm">Is your school not listed?</p>
                <p className="text-slate-500 text-xs mt-1">Contact us at <span className="text-[#4ABF4A]">info@muyikaratu.com</span> to onboard your school onto MEF EduSuite.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* School header */}
              <div className="flex items-center gap-4 p-5 rounded-2xl" style={{ background: `${selected.color}12`, border: `1px solid ${selected.color}33` }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl flex-shrink-0" style={{ background: `${selected.color}22` }}>{selected.emoji}</div>
                <div>
                  <h3 className="text-xl font-black text-white">{selected.name}</h3>
                  <p className="text-slate-400 text-sm">{selected.type}</p>
                  <p className="text-slate-500 text-xs mt-0.5">📍 {selected.location}</p>
                </div>
              </div>

              <p className="text-slate-300 text-sm leading-relaxed">{selected.description}</p>

              {/* Classes */}
              {selected.levels.length > 0 && (
                <div>
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Classes Available</p>
                  <div className="flex flex-wrap gap-2">
                    {selected.levels.map(lvl => (
                      <span key={lvl} className="px-3 py-1.5 rounded-xl text-xs font-bold" style={{ background: `${selected.color}18`, color: '#fff', border: `1px solid ${selected.color}33` }}>{lvl}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Admissions info or "not open" notice */}
              {selected.admissionsOpen && selected.fee && selected.deadline ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Application Fee', val: selected.fee, icon: '💳' },
                      { label: 'Deadline', val: selected.deadline, icon: '📅' },
                      { label: 'Email', val: selected.email, icon: '✉️' },
                      { label: 'Phone', val: selected.phone, icon: '📞' },
                    ].map((row, i) => (
                      <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <p className="text-slate-500 text-xs mb-1">{row.icon} {row.label}</p>
                        <p className="text-white text-sm font-bold">{row.val || '—'}</p>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { const s = selected!; setSelected(null); onClose(); onApply(s); }}
                    className="w-full py-4 rounded-2xl font-black text-white text-base transition-all hover:scale-[1.02] active:scale-95"
                    style={{ background: `linear-gradient(135deg,${selected.color},${selected.color}cc)`, boxShadow: `0 8px 24px ${selected.color}44` }}
                  >
                    Apply to {selected.short} — Start Application →
                  </button>
                </>
              ) : (
                <div className="rounded-2xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="text-3xl mb-3">🔔</div>
                  <p className="text-white font-black text-base mb-1">Application Not Open Yet</p>
                  <p className="text-slate-400 text-sm">This institution has not opened admissions on MEF EduSuite. Check back soon!</p>
                  {selected.email && (
                    <p className="text-slate-500 text-xs mt-3">For enquiries: <span className="text-[#4ABF4A]">{selected.email}</span></p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Video Recorder ───────────────────────────────────────────────────────────
const VideoRecorder: React.FC<{
  videoBlob: Blob | null;
  onRecorded: (blob: Blob) => void;
  onClear: () => void;
}> = ({ videoBlob, onRecorded, onClear }) => {
  const [phase, setPhase] = useState<'tips' | 'camera' | 'recording' | 'preview'>('tips');
  const [timeLeft, setTimeLeft] = useState(60);
  const [previewUrl, setPreviewUrl] = useState('');
  const [camError, setCamError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewUrlRef = useRef('');

  useEffect(() => {
    if ((phase === 'camera' || phase === 'recording') && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => { });
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const startCamera = async () => {
    setCamError('');
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      streamRef.current = s;
      setPhase('camera');
    } catch {
      setCamError('Camera access denied. Please allow camera & microphone access in your browser settings.');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4';
    try {
      const rec = new MediaRecorder(streamRef.current, { mimeType });
      recorderRef.current = rec;
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        onRecorded(blob);
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        setPhase('preview');
      };
      rec.start(200);
      setPhase('recording');
      let left = 60;
      setTimeLeft(left);
      timerRef.current = setInterval(() => {
        left -= 1;
        setTimeLeft(left);
        if (left <= 0) stopRecording();
      }, 1000);
    } catch {
      setCamError('Recording not supported. Please try Chrome or Firefox.');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
  };

  const reRecord = () => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = ''; }
    setPreviewUrl('');
    onClear();
    setPhase('tips');
    setTimeLeft(60);
    setCamError('');
  };

  const progressPct = Math.round(((60 - timeLeft) / 60) * 100);
  const timerColor = timeLeft <= 10 ? '#ef4444' : timeLeft <= 20 ? '#f59e0b' : '#10b981';

  return (
    <div>
      {phase === 'tips' && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-sm font-bold text-white">1-Minute Video CV</p>
            <p className="text-xs text-slate-400 mt-0.5">Record a short introduction — your first impression</p>
          </div>
          <div className="px-4 py-3 space-y-2.5">
            {([
              ['💡', 'Lighting', 'Face a bright window or lamp. Light must be in front of you — never behind you.'],
              ['🪑', 'Background', 'Clean, plain wall. Tidy visible space. Avoid busy patterns.'],
              ['👁', 'Camera', 'Eye level, 50–80 cm away. Prop your phone on a surface — don\'t hold it.'],
              ['🎙', 'Sound', 'Quiet room, no fan noise. Speak clearly at a moderate pace.'],
            ] as const).map(([icon, title, tip]) => (
              <div key={title} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 text-base mt-0.5">{icon}</span>
                <p className="text-xs text-slate-400 leading-relaxed"><span className="font-bold text-slate-200">{title}:</span> {tip}</p>
              </div>
            ))}
            <div className="mt-2 p-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)' }}>
              <p className="text-xs font-bold text-emerald-400 mb-1.5">Script outline (60 seconds)</p>
              <ol className="text-xs text-slate-400 space-y-0.5 list-decimal list-inside leading-relaxed">
                <li>Your name · qualification · years of experience (10s)</li>
                <li>Subject specialization and your teaching style (15s)</li>
                <li>One achievement or result you're proud of (15s)</li>
                <li>Why you want to join this school or platform (20s)</li>
              </ol>
            </div>
          </div>
          {camError && <p className="px-4 pb-2 text-xs text-red-400">{camError}</p>}
          <div className="px-4 pb-4">
            {videoBlob ? (
              <p className="text-xs text-emerald-400 text-center py-1">✓ Video already recorded — re-record below if needed</p>
            ) : (
              <button onClick={startCamera} className="w-full py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                Open Camera
              </button>
            )}
          </div>
        </div>
      )}

      {(phase === 'camera' || phase === 'recording') && (
        <div className="space-y-2">
          <div className="relative rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
            <video ref={videoRef} className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} muted playsInline />
            {phase === 'recording' && (
              <>
                <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.65)' }}>
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-xs font-bold text-white">REC</span>
                </div>
                <div className="absolute top-2.5 right-2.5 px-2.5 py-1 rounded-full text-xs font-mono font-bold" style={{ background: 'rgba(0,0,0,0.65)', color: timerColor, border: `1px solid ${timerColor}40` }}>
                  {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                  <div className="h-full transition-all duration-1000" style={{ width: `${progressPct}%`, background: timerColor }} />
                </div>
              </>
            )}
          </div>
          {phase === 'camera' && (
            <button onClick={startRecording} className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
              <span className="w-2.5 h-2.5 rounded-full bg-white" /> Start Recording (60s)
            </button>
          )}
          {phase === 'recording' && (
            <button onClick={stopRecording} className="w-full py-2.5 rounded-xl text-sm font-bold text-white border border-white/20" style={{ background: 'rgba(255,255,255,0.05)' }}>
              ⏹ Stop Early
            </button>
          )}
        </div>
      )}

      {phase === 'preview' && previewUrl && (
        <div className="space-y-2">
          <div className="relative rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
            <video src={previewUrl} className="w-full h-full object-cover" controls playsInline />
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: 'rgba(16,185,129,0.8)' }}>✓ Recorded</div>
          </div>
          <button onClick={reRecord} className="w-full py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition-all border border-white/10 hover:border-white/20">
            Re-record Video
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Teacher Application Modal ────────────────────────────────────────────────
const TeacherApplicationModal: React.FC<{
  open: boolean;
  onClose: () => void;
  schoolSettings: SchoolSettings;
  partnerSchools: PartnerSchool[];
  resolvedSchoolId?: string;
  resolvedSchoolName?: string;
}> = ({ open, onClose, schoolSettings, partnerSchools, resolvedSchoolId, resolvedSchoolName }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState('');
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', qualification: '', experience: '',
    subjects: '', teachingMode: 'both' as 'in-person' | 'online' | 'both',
    schoolId: resolvedSchoolId || '', schoolName: resolvedSchoolName || '', coverMessage: '',
    rateType: 'hourly' as 'hourly' | 'monthly' | 'both',
    currency: 'NGN', hourlyRate: '', monthlyRate: '',
  });

  // Re-sync school when resolved school changes (e.g. modal re-opened on a white-labeled domain)
  useEffect(() => {
    if (resolvedSchoolId) {
      setForm(f => ({ ...f, schoolId: resolvedSchoolId, schoolName: resolvedSchoolName || '' }));
    }
  }, [resolvedSchoolId, resolvedSchoolName]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const set = (key: string, val: string) => setForm(f => ({ ...f, [key]: val }));

  const canNext = () => {
    if (step === 1) return !!(form.fullName.trim() && form.email.trim() && form.phone.trim() && form.qualification && form.experience);
    if (step === 2) {
      if (!form.subjects.trim() || !form.teachingMode || !form.schoolId || !form.currency) return false;
      if ((form.rateType === 'hourly' || form.rateType === 'both') && !form.hourlyRate.trim()) return false;
      if ((form.rateType === 'monthly' || form.rateType === 'both') && !form.monthlyRate.trim()) return false;
      return true;
    }
    if (step === 3) return true;
    if (step === 4) return !!(form.coverMessage.trim() && confirmed);
    return true;
  };

  const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (file.type !== 'application/pdf') { setCvError('Please upload a PDF file only.'); return; }
    if (file.size > 500 * 1024) { setCvError(`File too large (${(file.size / 1024).toFixed(0)} KB). Maximum is 500 KB.`); return; }
    setCvError('');
    setCvFile(file);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const docRef = await addDoc(collection(db, 'teacherApplications'), {
        fullName: form.fullName,
        email: form.email.toLowerCase().trim(),
        phone: form.phone,
        qualification: form.qualification,
        experience: form.experience,
        subjects: form.subjects,
        teachingMode: form.teachingMode,
        schoolId: form.schoolId,
        schoolName: form.schoolName,
        coverMessage: form.coverMessage,
        rateType: form.rateType,
        currency: form.currency,
        ...(form.hourlyRate ? { hourlyRate: form.hourlyRate } : {}),
        ...(form.monthlyRate ? { monthlyRate: form.monthlyRate } : {}),
        status: 'pending',
        appliedAt: serverTimestamp(),
      });

      const updates: Record<string, string> = {};

      if (cvFile && storage) {
        try {
          const cvRef = storageRef(storage, `teacher_applications/${docRef.id}/cv.pdf`);
          await uploadBytes(cvRef, cvFile, { contentType: 'application/pdf' });
          updates.cvUrl = await getDownloadURL(cvRef);
        } catch (uploadErr) {
          console.warn('[teacherApply] CV upload failed (non-fatal):', uploadErr);
        }
      }

      if (videoBlob && storage) {
        try {
          const ext = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
          const vidRef = storageRef(storage, `teacher_applications/${docRef.id}/video.${ext}`);
          await uploadBytes(vidRef, videoBlob, { contentType: videoBlob.type || 'video/webm' });
          updates.videoUrl = await getDownloadURL(vidRef);
        } catch (uploadErr) {
          console.warn('[teacherApply] Video upload failed (non-fatal):', uploadErr);
        }
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'teacherApplications', docRef.id), updates);
      }

      setRefId(docRef.id.slice(0, 8).toUpperCase());
      setSubmitted(true);
    } catch (e: any) {
      console.error(e);
      setError('Submission failed. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1); setSubmitted(false); setRefId(''); setError(''); setConfirmed(false);
    setCvFile(null); setCvError(''); setVideoBlob(null);
    setForm({ fullName: '', email: '', phone: '', qualification: '', experience: '', subjects: '', teachingMode: 'both', schoolId: '', schoolName: '', coverMessage: '', rateType: 'hourly', currency: 'NGN', hourlyRate: '', monthlyRate: '' });
    onClose();
  };

  if (!open) return null;

  const STEP_LABELS = ['Your Details', 'Compensation', 'Documents', 'Profile'];
  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500/60 transition-all";
  const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";
  const CURRENCIES = ['NGN', 'USD', 'GBP', 'EUR', 'CAD', 'AED', 'SAR', 'GHS', 'KES'];
  const showHourly = form.rateType === 'hourly' || form.rateType === 'both';
  const showMonthly = form.rateType === 'monthly' || form.rateType === 'both';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}>
      <div className="relative w-full max-w-lg max-h-[94vh] flex flex-col rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(145deg,#0a0f1e,#0f172a)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 100px rgba(0,0,0,0.7)' }}>
        <div className="h-1 w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg,#10b981,#06b6d4,#8b5cf6)' }} />
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]" aria-hidden="true">
          <defs><pattern id="tap" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse"><polygon points="30,4 36,22 54,22 40,34 46,52 30,42 14,52 20,34 6,22 24,22" fill="none" stroke="white" strokeWidth="0.8" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#tap)" />
        </svg>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between px-6 py-5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <h2 className="text-lg font-black text-white">Apply to Teach</h2>
            <p className="text-slate-400 text-sm">{schoolSettings.schoolName || brand.appName} · Tutor Programme</p>
          </div>
          <button onClick={reset} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all text-lg">✕</button>
        </div>

        {/* Step indicator */}
        {!submitted && (
          <div className="relative z-10 px-6 pt-4 flex-shrink-0">
            <div className="flex items-center gap-1 mb-4">
              {STEP_LABELS.map((lbl, i) => {
                const n = i + 1; const active = step === n; const done = step > n;
                return (
                  <React.Fragment key={n}>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${active ? 'bg-emerald-500 text-white' : done ? 'bg-emerald-900 text-emerald-400' : 'bg-white/8 text-slate-500'}`}>
                        {done ? '✓' : n}
                      </div>
                      <span className={`text-xs font-semibold hidden sm:block ${active ? 'text-white' : 'text-slate-500'}`}>{lbl}</span>
                    </div>
                    {i < 3 && <div className={`flex-1 h-px transition-all ${done ? 'bg-emerald-600' : 'bg-white/10'}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="relative z-10 flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 0 40px rgba(16,185,129,0.4)' }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><polyline points="5,14 11,20 23,8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <h3 className="text-2xl font-black text-white mb-2">Application Submitted!</h3>
              <p className="text-slate-400 text-sm mb-4">We'll review your application within 3–5 business days.</p>
              <div className="px-5 py-3 rounded-2xl mb-6" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)' }}>
                <p className="text-xs text-slate-400 mb-0.5">Reference ID</p>
                <p className="text-lg font-black text-emerald-400 tracking-widest">{refId}</p>
              </div>
              <p className="text-xs text-slate-500 mb-6">Save this reference ID for follow-up enquiries.</p>
              <button onClick={reset} className="px-8 py-3 rounded-xl font-bold text-sm text-white" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>Close</button>
            </div>
          ) : (
            <>
              {/* STEP 1 — Details */}
              {step === 1 && (
                <div className="space-y-4 pt-1">
                  <div><label className={labelCls}>Full Name</label><input className={inputCls} placeholder="e.g. Fatima Al-Hassan" value={form.fullName} onChange={e => set('fullName', e.target.value)} /></div>
                  <div><label className={labelCls}>Email Address</label><input className={inputCls} type="email" placeholder="your@email.com" value={form.email} onChange={e => set('email', e.target.value)} /></div>
                  <div><label className={labelCls}>Phone Number</label><input className={inputCls} placeholder="+234 800 000 0000" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                  <div>
                    <label className={labelCls}>Highest Qualification</label>
                    <select className={inputCls} value={form.qualification} onChange={e => set('qualification', e.target.value)}>
                      <option value="">Select qualification…</option>
                      {['SSCE', 'ND/NCE', 'B.Sc/B.Ed', 'M.Sc/M.Ed', 'PhD', 'Professional Cert', 'Other'].map(q => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Years of Experience</label>
                    <select className={inputCls} value={form.experience} onChange={e => set('experience', e.target.value)}>
                      <option value="">Select experience…</option>
                      {['Less than 1 year', '1-2 years', '3-5 years', '5-10 years', '10+ years'].map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* STEP 2 — Teaching + Compensation */}
              {step === 2 && (
                <div className="space-y-4 pt-1">
                  <div>
                    <label className={labelCls}>Subjects / Specialization</label>
                    <textarea className={inputCls} rows={2} placeholder="e.g. Mathematics, Physics, Further Maths" value={form.subjects} onChange={e => set('subjects', e.target.value)} style={{ resize: 'none' }} />
                  </div>
                  <div>
                    <label className={labelCls}>Teaching Mode</label>
                    <div className="flex gap-2">
                      {(['in-person', 'online', 'both'] as const).map(m => (
                        <button key={m} type="button" onClick={() => set('teachingMode', m)} className={`flex-1 py-2 rounded-xl text-sm font-bold capitalize transition-all border ${form.teachingMode === m ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white/5 text-slate-400 border-white/10 hover:border-white/20'}`}>
                          {m === 'in-person' ? 'In-Person' : m === 'online' ? 'Online' : 'Both'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {resolvedSchoolId ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}>
                      <span>🏫</span>
                      <span>{resolvedSchoolName || resolvedSchoolId}</span>
                      <span className="ml-auto text-xs opacity-60">Auto-detected</span>
                    </div>
                  ) : (
                    <div>
                      <label className={labelCls}>Which School / Platform</label>
                      <select className={inputCls} value={form.schoolId} onChange={e => {
                        const s = partnerSchools.find(x => x.id === e.target.value);
                        set('schoolId', e.target.value); set('schoolName', s?.name || '');
                      }}>
                        <option value="">Select school…</option>
                        {partnerSchools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Compensation Expectations</p>
                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Payment Type</label>
                        <div className="flex gap-2">
                          {(['hourly', 'monthly', 'both'] as const).map(rt => (
                            <button key={rt} type="button" onClick={() => set('rateType', rt)} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all border ${form.rateType === rt ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white/5 text-slate-400 border-white/10 hover:border-white/20'}`}>
                              {rt === 'hourly' ? 'Per Hour' : rt === 'monthly' ? 'Monthly' : 'Both'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Currency</label>
                        <select className={inputCls} value={form.currency} onChange={e => set('currency', e.target.value)}>
                          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      {showHourly && (
                        <div>
                          <label className={labelCls}>Expected Hourly Rate ({form.currency})</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">{form.currency}</span>
                            <input className={inputCls} style={{ paddingLeft: '3.5rem' }} type="number" min="0" placeholder="e.g. 5000" value={form.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} />
                          </div>
                        </div>
                      )}
                      {showMonthly && (
                        <div>
                          <label className={labelCls}>Expected Monthly Salary ({form.currency})</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">{form.currency}</span>
                            <input className={inputCls} style={{ paddingLeft: '3.5rem' }} type="number" min="0" placeholder="e.g. 150000" value={form.monthlyRate} onChange={e => set('monthlyRate', e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 — Documents */}
              {step === 3 && (
                <div className="space-y-5 pt-1">
                  <div>
                    <label className={labelCls}>CV / Résumé <span className="normal-case font-normal text-slate-600">— PDF only, max 500 KB</span></label>
                    <label className={`flex flex-col items-center justify-center gap-2 py-5 rounded-xl cursor-pointer transition-all ${cvFile ? 'border-2 border-emerald-500/50' : 'border-2 border-dashed border-white/10 hover:border-white/20'}`} style={cvFile ? { background: 'rgba(16,185,129,0.05)' } : { background: 'rgba(255,255,255,0.02)' }}>
                      <input type="file" accept=".pdf,application/pdf" className="sr-only" onChange={handleCvChange} />
                      {cvFile ? (
                        <><span className="text-2xl">📄</span><p className="text-sm font-bold text-emerald-400">{cvFile.name}</p><p className="text-xs text-slate-400">{(cvFile.size / 1024).toFixed(0)} KB · PDF</p><p className="text-xs text-slate-500">Click to replace</p></>
                      ) : (
                        <><span className="text-2xl">📎</span><p className="text-sm text-slate-300 font-semibold">Click to upload your CV</p><p className="text-xs text-slate-500">PDF only · max 500 KB</p></>
                      )}
                    </label>
                    {cvError && <p className="text-red-400 text-xs mt-1">{cvError}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Video CV <span className="normal-case font-normal text-slate-600">— 1 minute, recorded in-browser</span></label>
                    <VideoRecorder videoBlob={videoBlob} onRecorded={setVideoBlob} onClear={() => setVideoBlob(null)} />
                  </div>
                  <p className="text-xs text-slate-500 text-center">Both are optional but strongly recommended — they significantly improve your chances.</p>
                </div>
              )}

              {/* STEP 4 — Profile */}
              {step === 4 && (
                <div className="space-y-4 pt-1">
                  <div>
                    <label className={labelCls}>Cover Message</label>
                    <p className="text-xs text-slate-500 mb-2">Tell us why you want to teach here — your philosophy, what makes you unique</p>
                    <textarea className={inputCls} rows={7} placeholder="Share your teaching philosophy, relevant experience, and why you want to join our platform…" value={form.coverMessage} onChange={e => set('coverMessage', e.target.value)} style={{ resize: 'none' }} />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mt-0.5 transition-all border ${confirmed ? 'bg-emerald-500 border-emerald-500' : 'bg-white/5 border-white/20 group-hover:border-white/40'}`} onClick={() => setConfirmed(c => !c)}>
                      {confirmed && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <span className="text-sm text-slate-400">I confirm all information provided is accurate and truthful.</span>
                  </label>
                  {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="relative z-10 flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={() => step > 1 ? setStep(s => s - 1) : reset()} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-400 hover:text-white hover:bg-white/8 transition-all border border-white/10">
              {step === 1 ? 'Cancel' : '← Back'}
            </button>
            {step < 4 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                Next →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={!canNext() || submitting} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40 flex items-center gap-2" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                {submitting ? <><Spinner /><span>Submitting…</span></> : 'Submit Application'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const LandingPage: React.FC<LandingPageProps> = ({ onLogin, schoolSettings, loading, schools }) => {
  const pricing = usePricing();
  const ngn = (n: number) => `₦${n.toLocaleString('en-NG')}`;
  const { currentSchool } = useSchool();
  const isWhiteLabeled = !!(currentSchool && currentSchool.id !== DEFAULT_SCHOOL_ID);

  const [loginOpen, setLoginOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [selectedApplySchool, setSelectedApplySchool] = useState<PartnerSchool | null>(null);
  const [teacherApplyOpen, setTeacherApplyOpen] = useState(false);
  const [partnerApplyOpen, setPartnerApplyOpen] = useState(false);
  const [partnerSchools, setPartnerSchools] = useState<PartnerSchool[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [activeNav, setActiveNav] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Announcement ticker rotation
  useEffect(() => {
    const timer = setInterval(() => setTickerIndex(i => (i + 1) % ANNOUNCEMENTS.length), 5000);
    return () => clearInterval(timer);
  }, []);

  // Navbar scroll effect
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch active schools from Firestore on mount (used both in the in-page section and the modal)
  useEffect(() => {
    setLoadingSchools(true);
    getDocs(collection(db, 'schools'))
      .then(snap => {
        const list: PartnerSchool[] = snap.docs
          .filter(d => { const s = d.data().subscriptionStatus; return s !== 'suspended' && s !== 'denied'; })
          .map(d => {
            const data = d.data() as any;
            const isCciet = d.id.toLowerCase().includes('cciet') || (data.abbreviation || '').toLowerCase() === 'cciet';
            const detail = SCHOOL_DETAILS[d.id] || (isCciet ? CCIET_DETAIL : {});
            return {
              id: d.id,
              name: data.name || 'Unknown School',
              short: detail.short || (data.abbreviation || data.name?.split(' ').map((w: string) => w[0]).join('').slice(0, 4) || 'SCH'),
              location: detail.location || data.address || data.settings?.schoolAddress || 'Kaduna, Nigeria',
              type: detail.type || data.institutionType || 'School',
              levels: detail.levels || [],
              description: detail.description || `${data.name} is an active institution on MEF EduSuite.`,
              email: data.contactEmail || data.settings?.schoolEmail || '',
              phone: data.contactPhone || data.settings?.schoolPhone || '',
              color: detail.color || '#1B3870',
              emoji: detail.emoji || '🏫',
              admissionsOpen: Array.isArray(data.enabledFeatures) ? data.enabledFeatures.includes('admissions') : true,
              fee: detail.fee || '',
              deadline: detail.deadline || '',
            } as PartnerSchool;
          });
        setPartnerSchools(list);
      })
      .catch(() => setPartnerSchools([]))
      .finally(() => setLoadingSchools(false));
  }, []);

  const scrollTo = (id: string) => {
    setActiveNav(id);
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(135deg,#011a13 0%,#0f172a 55%,#1e1b4b 100%)' }}>
        {/* Ambient blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[10%] left-[8%] w-72 h-72 rounded-full bg-emerald-600/15 blur-[80px]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
          <div className="absolute bottom-[12%] right-[10%] w-64 h-64 rounded-full bg-violet-600/12 blur-[80px]" style={{ animation: 'pulse 4s ease-in-out infinite', animationDelay: '1s' }} />
          <div className="absolute top-[45%] right-[30%] w-48 h-48 rounded-full bg-cyan-600/10 blur-[60px]" style={{ animation: 'pulse 3.5s ease-in-out infinite', animationDelay: '0.5s' }} />
        </div>

        {/* Card */}
        <div className="relative flex flex-col items-center gap-6 px-12 py-10 rounded-3xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', backdropFilter: 'blur(24px)', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}>

          {/* Logo with pulse ring */}
          <div className="relative">
            {schoolSettings.logoDataUrl ? (
              <img src={schoolSettings.logoDataUrl} alt="Logo" className="w-20 h-20 rounded-2xl object-contain" style={{ filter: 'drop-shadow(0 0 24px rgba(16,185,129,0.45))' }} />
            ) : (
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl font-black text-white" style={{ background: 'linear-gradient(135deg,#1B3870,#3D9B3D)', boxShadow: '0 0 40px rgba(27,56,112,0.5)' }}>
                MEF
              </div>
            )}
            <div className="absolute -inset-2.5 rounded-3xl border border-emerald-500/40" style={{ animation: 'ping 2s cubic-bezier(0,0,0.2,1) infinite' }} />
          </div>

          {/* Title */}
          <div className="text-center">
            <h1 className="text-xl font-black text-white tracking-wide">{schoolSettings.schoolName || 'MEF EduSuite'}</h1>
            <p className="text-emerald-400/70 text-xs mt-1 tracking-widest uppercase font-semibold">Student · Teacher · Admin Portal</p>
          </div>

          {/* Sliding progress bar */}
          <div className="w-52 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full" style={{
              width: '55%',
              background: 'linear-gradient(90deg,#10b981,#06b6d4,#8b5cf6)',
              animation: 'kcaSlide 1.5s ease-in-out infinite',
            }} />
          </div>

          <p className="text-slate-500 text-xs tracking-widest uppercase" style={{ animation: 'pulse 2s ease-in-out infinite' }}>Restoring your session…</p>
        </div>

        <style>{`
          @keyframes kcaSlide {
            0%   { transform: translateX(-130%); }
            100% { transform: translateX(260%); }
          }
        `}</style>
      </div>
    );
  }

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'news', label: 'News' },
    { id: 'admissions', label: 'Schools' },
  ];

  return (
    <>
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onLogin={onLogin} />
      <SchoolsModal open={applyOpen} onClose={() => setApplyOpen(false)} onApply={(s: PartnerSchool) => { setSelectedApplySchool(s); setApplyOpen(false); setApplicationOpen(true); }} schools={partnerSchools} loadingSchools={loadingSchools} />
      <ApplicationModal open={applicationOpen} onClose={() => setApplicationOpen(false)} schoolSettings={schoolSettings} school={selectedApplySchool} />
      <SchoolPartnershipModal open={partnerApplyOpen} onClose={() => setPartnerApplyOpen(false)} />
      <TeacherApplicationModal
        open={teacherApplyOpen}
        onClose={() => setTeacherApplyOpen(false)}
        schoolSettings={schoolSettings}
        partnerSchools={partnerSchools}
        resolvedSchoolId={isWhiteLabeled ? currentSchool?.id : undefined}
        resolvedSchoolName={isWhiteLabeled ? (currentSchool?.name || undefined) : undefined}
      />

      <div className="min-h-screen text-white" style={{ background: '#020c18' }}>

        {/* ══ NAVBAR ══════════════════════════════════════════════════════════ */}
        <nav
          className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300"
          style={{
            background: scrolled ? 'rgba(2,12,24,0.95)' : 'transparent',
            backdropFilter: scrolled ? 'blur(20px)' : 'none',
            borderBottom: scrolled ? '1px solid rgba(16,185,129,0.15)' : 'none',
            boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.4)' : 'none',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo — always MEF brand */}
              <div className="flex items-center gap-3">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <rect width="36" height="36" rx="8" fill="#1B3870" />
                  <path d="M4 26 L4 12 L9 20 L14 12 L14 26" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M9 20 L14 12 L14 26" stroke="#4ABF4A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M17 12 L17 26 M17 12 L23 12 M17 19 L22 19 M17 26 L23 26" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M26 12 L26 26 M26 12 L32 12 M26 19 L31 19" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <circle cx="32" cy="10" r="2" fill="#E8B400" />
                </svg>
                <div className="hidden sm:block">
                  {isWhiteLabeled ? (
                    <>
                      <p className="font-black text-white text-sm leading-tight">{currentSchool?.name}</p>
                      <p className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#3D9B3D' }}>Powered by MEF EduSuite</p>
                    </>
                  ) : (
                    <>
                      <p className="font-black text-white text-sm leading-tight">MEF EduSuite</p>
                      <p className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#3D9B3D' }}>Education · Empowerment · Growth</p>
                    </>
                  )}
                </div>
              </div>

              {/* Desktop nav links */}
              <div className="hidden md:flex items-center gap-1">
                {navLinks.map(link => (
                  <button
                    key={link.id}
                    onClick={() => {
                      if (link.id === 'admissions') {
                        isWhiteLabeled ? setApplicationOpen(true) : setApplyOpen(true);
                      } else {
                        scrollTo(link.id);
                      }
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeNav === link.id ? 'text-emerald-400 bg-emerald-500/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {/* Right controls */}
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <button
                  onClick={() => setLoginOpen(true)}
                  className="px-5 py-2 rounded-xl font-bold text-sm text-white transition-all hover:scale-105 active:scale-95"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}
                >
                  Sign In
                </button>
                {/* Mobile menu toggle */}
                <button
                  onClick={() => setMobileMenuOpen(v => !v)}
                  className="md:hidden w-9 h-9 rounded-lg flex flex-col items-center justify-center gap-1.5 hover:bg-white/10 transition-all"
                >
                  <span className={`block w-5 h-0.5 bg-white transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                  <span className={`block w-5 h-0.5 bg-white transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                  <span className={`block w-5 h-0.5 bg-white transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                </button>
              </div>
            </div>

            {/* Mobile menu dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden pb-4 animate-slide-up" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                {navLinks.map(link => (
                  <button
                    key={link.id}
                    onClick={() => { setMobileMenuOpen(false); link.id === 'admissions' ? setApplyOpen(true) : scrollTo(link.id); }}
                    className="w-full text-left px-4 py-3 text-sm font-semibold text-slate-300 hover:text-white hover:bg-white/5 transition-all"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* ══ HERO — Cinematic 3D Carousel ════════════════════════════════════ */}
        <CinematicHeroCarousel
          onLoginClick={() => setLoginOpen(true)}
          onApplyClick={() => setApplyOpen(true)}
        />

        {/* ══ ANNOUNCEMENT TICKER ═════════════════════════════════════════════ */}
        <div className="relative z-10 py-3 overflow-hidden" style={{ background: 'linear-gradient(90deg,#064e3b,#065f46,#064e3b)', borderTop: '1px solid rgba(16,185,129,0.3)', borderBottom: '1px solid rgba(16,185,129,0.3)' }}>
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-4">
            <span className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase text-black" style={{ background: '#10b981' }}>
              LIVE
            </span>
            <div className="relative overflow-hidden flex-1 h-6">
              {ANNOUNCEMENTS.map((ann, i) => (
                <p
                  key={i}
                  className="absolute inset-0 text-sm font-semibold text-emerald-100 transition-all duration-700 truncate"
                  style={{ opacity: i === tickerIndex ? 1 : 0, transform: i === tickerIndex ? 'translateY(0)' : 'translateY(20px)' }}
                >
                  {ann}
                </p>
              ))}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {ANNOUNCEMENTS.map((_, i) => (
                <button key={i} onClick={() => setTickerIndex(i)} className="w-1.5 h-1.5 rounded-full transition-all" style={{ background: i === tickerIndex ? '#10b981' : 'rgba(255,255,255,0.3)' }} />
              ))}
            </div>
          </div>
        </div>

        {/* ══ STATS BAR ═══════════════════════════════════════════════════════ */}
        <div className="relative py-12 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((stat, i) => (
              <div key={i} className="group text-center relative rounded-2xl p-6 overflow-hidden cursor-default transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {/* shimmer */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
                <div className="text-4xl mb-3 animate-float" style={{ animationDelay: `${i * 0.5}s` }}>{stat.icon}</div>
                <p className="text-3xl font-black text-white animate-number-ping">{stat.value}</p>
                <p className="text-slate-400 text-sm font-semibold mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ CURRENT TERM BANNER ═════════════════════════════════════════════ */}
        <div className="py-6 px-4" style={{ background: 'linear-gradient(90deg,#1e1b4b,#2e1065,#1e1b4b)', borderTop: '1px solid rgba(139,92,246,0.2)', borderBottom: '1px solid rgba(139,92,246,0.2)' }}>
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)' }}>
                📅
              </div>
              <div>
                <p className="text-violet-300 text-xs font-bold uppercase tracking-widest">Current Academic Period</p>
                <p className="text-white font-black text-lg">{TERM_INFO.term} — {TERM_INFO.session} Session</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 text-center">
              <div>
                <p className="text-violet-300 text-xs font-bold uppercase tracking-wide">Week</p>
                <p className="text-white font-black">{TERM_INFO.weekNumber} / {TERM_INFO.totalWeeks}</p>
              </div>
              <div>
                <p className="text-violet-300 text-xs font-bold uppercase tracking-wide">Exams Start</p>
                <p className="text-white font-black">{TERM_INFO.examStart}</p>
              </div>
              <div>
                <p className="text-violet-300 text-xs font-bold uppercase tracking-wide">Next Resumption</p>
                <p className="text-white font-black">{TERM_INFO.resumption}</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full mt-3">
              <div className="flex justify-between text-xs text-slate-400 mb-1 font-semibold">
                <span>Term Progress</span>
                <span>{Math.round((TERM_INFO.weekNumber / TERM_INFO.totalWeeks) * 100)}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(TERM_INFO.weekNumber / TERM_INFO.totalWeeks) * 100}%`,
                    background: 'linear-gradient(90deg,#8b5cf6,#06b6d4)',
                    boxShadow: '0 0 12px rgba(139,92,246,0.5)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ══ ABOUT US ════════════════════════════════════════════════════════ */}
        <section id="about" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#020c18,#0f172a)' }}>
          <StarPattern opacity={0.05} />
          <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-16 items-center relative z-10">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-bold" style={{ background: 'rgba(27,56,112,0.2)', border: '1px solid rgba(27,56,112,0.4)', color: '#93c5fd' }}>
                🏛️ Who We Are
              </div>
              <h2 className="text-4xl font-black text-white mb-6 leading-tight">
                Education for<br />
                <span style={{ background: 'linear-gradient(135deg,#3D9B3D,#86efac)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Every Nigerian
                </span>
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                Muyikaratu Educational Foundation (MEF) is a registered NGO (CAC No. 181465 · Permit MBEP/NGO-IC/1895/Vol.1/39) dedicated to promoting quality education in Nigeria — particularly in Northern Nigeria — through technology that removes barriers of tribe, language, and background.
              </p>
              <p className="text-slate-400 leading-relaxed mb-8">
                From retraining vulnerable communities in ICT to connecting professional instructors with eager learners, MEF EduSuite is the digital backbone of our mission: improving lives through learning.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: '📚', label: 'Promote Education', text: 'Expanding learning access across Northern Nigeria' },
                  { icon: '💻', label: 'ICT Capacity', text: 'Retraining vulnerable communities in technology' },
                  { icon: '🤝', label: 'Inclusive Access', text: 'Regardless of tribe, language, or religion' },
                  { icon: '🔗', label: 'Connect & Grow', text: 'Linking instructors with learners nationwide' },
                ].map((item, i) => (
                  <div key={i} className="group flex items-start gap-3 p-4 rounded-2xl transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <span className="text-2xl mt-0.5">{item.icon}</span>
                    <div>
                      <p className="font-bold text-white text-sm">{item.label}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual card */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden p-8" style={{ background: 'linear-gradient(135deg,rgba(27,56,112,0.2),rgba(61,155,61,0.08))', border: '1px solid rgba(27,56,112,0.35)' }}>
                <StarPattern opacity={0.05} />
                <div className="relative z-10 space-y-5">
                  {[
                    { label: 'Platform Uptime', val: 99, color: '#3D9B3D' },
                    { label: 'Learner Satisfaction', val: 96, color: '#1B3870' },
                    { label: 'Course Completion Rate', val: 82, color: '#E8B400' },
                    { label: 'Qualified Instructors', val: 100, color: '#4ABF4A' },
                  ].map((bar, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-semibold text-slate-300">{bar.label}</span>
                        <span className="font-black" style={{ color: bar.color }}>{bar.val}%</span>
                      </div>
                      <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div className="h-full rounded-full" style={{ width: `${bar.val}%`, background: `linear-gradient(90deg,${bar.color},${bar.color}88)`, boxShadow: `0 0 10px ${bar.color}66` }} />
                      </div>
                    </div>
                  ))}

                  <div className="grid grid-cols-3 gap-3 mt-6 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {[
                      { n: '2022', l: 'Founded' },
                      { n: 'NGO', l: 'Registered' },
                      { n: 'KD', l: 'Nigeria' },
                    ].map((s, i) => (
                      <div key={i} className="text-center">
                        <p className="text-2xl font-black text-white">{s.n}</p>
                        <p className="text-slate-400 text-xs font-semibold">{s.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ STAR OF THE WEEK + TEACHER OF WEEK ══════════════════════════════ */}
        <section className="relative py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-bold text-amber-400" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}>
                ✨ Weekly Spotlights
              </div>
              <h2 className="text-3xl font-black text-white">Heroes of the Week</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">

              {/* Student of the week */}
              <div className="group relative rounded-3xl overflow-hidden p-6 transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg,rgba(245,158,11,0.08),rgba(251,191,36,0.04))', border: '1px solid rgba(245,158,11,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-amber-400/5 to-transparent pointer-events-none" />

                {/* Header badge */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black text-amber-900" style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
                    🏆 STUDENT OF THE WEEK
                  </div>
                  <span className="text-2xl animate-float">{STUDENT_OF_WEEK.badge}</span>
                </div>

                {/* Profile */}
                <div className="flex items-start gap-5">
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl animate-float" style={{ background: 'linear-gradient(135deg,rgba(245,158,11,0.2),rgba(251,191,36,0.1))', border: '2px solid rgba(245,158,11,0.4)' }}>
                      {STUDENT_OF_WEEK.avatar}
                    </div>
                    {/* glow ring */}
                    <div className="absolute -inset-1 rounded-2xl pointer-events-none animate-glow-amber opacity-50" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-black text-white">{STUDENT_OF_WEEK.name}</h3>
                    <p className="text-amber-400 font-bold text-sm">{STUDENT_OF_WEEK.class}</p>
                    <p className="text-slate-400 text-sm mt-2 leading-relaxed">{STUDENT_OF_WEEK.achievement}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mt-6 pt-5" style={{ borderTop: '1px solid rgba(245,158,11,0.15)' }}>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.08)' }}>
                    <p className="text-2xl font-black text-amber-400">{STUDENT_OF_WEEK.score}%</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Top Score</p>
                  </div>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.08)' }}>
                    <p className="text-2xl font-black text-amber-400">{STUDENT_OF_WEEK.juzCompleted}</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Juz Memorized</p>
                  </div>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.08)' }}>
                    <p className="text-2xl font-black text-amber-400">{STUDENT_OF_WEEK.streak}</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Day Streak 🔥</p>
                  </div>
                </div>
              </div>

              {/* Teacher of the week */}
              <div className="group relative rounded-3xl overflow-hidden p-6 transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(109,40,217,0.04))', border: '1px solid rgba(139,92,246,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-violet-400/5 to-transparent pointer-events-none" />

                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black text-violet-900" style={{ background: 'linear-gradient(135deg,#a78bfa,#8b5cf6)' }}>
                    ⭐ TEACHER OF THE WEEK
                  </div>
                  <span className="text-2xl animate-float" style={{ animationDelay: '1s' }}>{TEACHER_OF_WEEK.badge}</span>
                </div>

                <div className="flex items-start gap-5">
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl animate-float" style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.2),rgba(109,40,217,0.1))', border: '2px solid rgba(139,92,246,0.4)', animationDelay: '1s' }}>
                      {TEACHER_OF_WEEK.avatar}
                    </div>
                    <div className="absolute -inset-1 rounded-2xl pointer-events-none" style={{ boxShadow: '0 0 20px rgba(139,92,246,0.3)', animation: 'glow-pulse-amber 2.5s ease-in-out infinite' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-black text-white">{TEACHER_OF_WEEK.name}</h3>
                    <p className="text-violet-400 font-bold text-sm">{TEACHER_OF_WEEK.subject}</p>
                    <p className="text-slate-500 text-xs">{TEACHER_OF_WEEK.department}</p>
                    <p className="text-slate-400 text-sm mt-2 leading-relaxed">{TEACHER_OF_WEEK.achievement}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-6 pt-5" style={{ borderTop: '1px solid rgba(139,92,246,0.15)' }}>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(139,92,246,0.08)' }}>
                    <p className="text-2xl font-black text-violet-400">{TEACHER_OF_WEEK.studentsHelped}</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Students</p>
                  </div>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(139,92,246,0.08)' }}>
                    <p className="text-2xl font-black text-violet-400">{TEACHER_OF_WEEK.rating}</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Rating ⭐</p>
                  </div>
                  <div className="text-center rounded-xl p-3" style={{ background: 'rgba(139,92,246,0.08)' }}>
                    <p className="text-2xl font-black text-violet-400">100%</p>
                    <p className="text-slate-400 text-[10px] font-semibold uppercase mt-0.5">Pass Rate</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ══ NEWS & EVENTS ═══════════════════════════════════════════════════ */}
        <section id="news" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-bold text-blue-400" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)' }}>
                📰 Latest from MEF
              </div>
              <h2 className="text-4xl font-black text-white">News &amp; Events</h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {NEWS.map((item) => (
                <div key={item.id} className="group relative rounded-3xl overflow-hidden flex flex-col transition-all hover:scale-[1.03] hover:-translate-y-1 cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {/* shimmer */}
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />

                  {/* Color accent top */}
                  <div className="h-1 w-full" style={{ background: item.color }} />

                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: `${item.color}22`, color: item.color, border: `1px solid ${item.color}44` }}>
                        {item.category}
                      </span>
                      <span className="text-3xl">{item.emoji}</span>
                    </div>

                    <h3 className="font-black text-white text-sm leading-snug mb-3 group-hover:text-emerald-300 transition-colors">{item.title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed flex-1">{item.excerpt}</p>

                    <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <span className="text-slate-500 text-xs">{item.date}</span>
                      <span className="text-xs font-bold transition-all group-hover:translate-x-1" style={{ color: item.color }}>Read →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ PARTNER SCHOOLS ═════════════════════════════════════════════════ */}
        {!isWhiteLabeled && <section id="admissions" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0b1530,#0f172a,#0b1530)' }}>
          <StarPattern opacity={0.05} />
          <div className="absolute top-1/2 left-1/4 w-[500px] h-[300px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse,rgba(27,56,112,0.2),transparent 70%)', filter: 'blur(50px)' }} />

          <div className="max-w-5xl mx-auto px-4 relative z-10">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-bold" style={{ background: 'rgba(27,56,112,0.2)', border: '1px solid rgba(27,56,112,0.4)', color: '#93c5fd' }}>
                🌍 Partner Institutions — Nigeria · Canada · China
              </div>
              <h2 className="text-4xl font-black text-white mb-4">Find Your Institution</h2>
              <p className="text-slate-300 text-lg max-w-xl mx-auto">MEF EduSuite powers learning management for schools and training institutions across Nigeria, Canada, and China. Select an institution below to explore their programmes.</p>
            </div>

            {/* School cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {partnerSchools.map(school => (
                <div key={school.id} className="group relative rounded-3xl overflow-hidden p-6 transition-all hover:scale-[1.02] hover:-translate-y-1" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${school.color}33` }}>
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none opacity-10" style={{ background: `radial-gradient(circle,${school.color},transparent)`, filter: 'blur(20px)' }} />

                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0" style={{ background: `${school.color}22`, border: `1px solid ${school.color}44` }}>
                      {school.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-black text-white text-lg">{school.name}</h3>
                        {school.admissionsOpen && (
                          <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: '#3D9B3D22', color: '#4ABF4A', border: '1px solid #3D9B3D55' }}>ADMISSIONS OPEN</span>
                        )}
                        {school.short === 'CCIET' && (
                          <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(200,16,46,0.15)', color: '#f87171', border: '1px solid rgba(200,16,46,0.4)' }}>🍁 INTERNATIONAL</span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{school.type}</p>
                      <p className="text-slate-500 text-xs mt-0.5">📍 {school.location}</p>
                    </div>
                  </div>

                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{school.description}</p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {school.levels.map(lvl => (
                      <span key={lvl} className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: `${school.color}15`, color: '#cbd5e1', border: `1px solid ${school.color}30` }}>{lvl}</span>
                    ))}
                  </div>

                  <button
                    onClick={() => setApplyOpen(true)}
                    className="w-full py-3 rounded-xl font-black text-sm text-white transition-all hover:scale-[1.02]"
                    style={{ background: `linear-gradient(135deg,${school.color},${school.color}bb)`, boxShadow: `0 4px 20px ${school.color}44` }}
                  >
                    View Details &amp; Apply →
                  </button>
                </div>
              ))}

              {/* Placeholder — partner CTA */}
              <div className="rounded-3xl p-6 flex flex-col items-center justify-center text-center gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(27,56,112,0.4)' }}>
                <div className="text-4xl">🏫</div>
                <p className="text-slate-300 font-black text-lg">Your institution could be here</p>
                <p className="text-slate-500 text-sm">Schools and training institutions worldwide are welcome to join. Apply for a white-label partnership and give your learners access to world-class, IQA-certified tools.</p>
                <button
                  onClick={() => setPartnerApplyOpen(true)}
                  className="mt-2 px-6 py-3 rounded-xl text-sm font-black text-white transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg,#1B3870,#254d9e)', boxShadow: '0 4px 16px rgba(27,56,112,0.4)' }}
                >
                  Apply to Partner →
                </button>
              </div>
            </div>
          </div>
        </section>}

        {/* ══ TANG MOU PARTNERSHIP ════════════════════════════════════════════ */}
        <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#1a0008,#0f172a,#1a0008)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(196,30,58,0.08), transparent)' }} />
          <StarPattern opacity={0.04} />
          <div className="max-w-5xl mx-auto px-4 relative z-10">
            {/* Header */}
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-bold" style={{ background: 'rgba(196,30,58,0.15)', border: '1px solid rgba(196,30,58,0.35)', color: '#fca5a5' }}>
                🤝 Signed MOU · September 9, 2025
              </div>
              <h2 className="text-4xl font-black text-white mb-4">Historic China–Nigeria Education Partnership</h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">The Muyikaratu Educational Foundation has signed a landmark 5-year Memorandum of Understanding with TANG Chinese Education & Technology Ltd., Beijing — bridging Chinese and Nigerian education for global impact.</p>
            </div>

            {/* Partner logos banner */}
            <div className="flex items-center justify-center gap-6 mb-12 flex-wrap">
              {/* MEF side */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(27,56,112,0.3)', border: '1px solid rgba(27,56,112,0.5)' }}>
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="44" height="44" rx="10" fill="#1B3870" />
                    <path d="M5 32 L5 14 L11 24 L17 14 L17 32" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M11 24 L17 14 L17 32" stroke="#4ABF4A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M21 14 L21 32 M21 14 L29 14 M21 23 L28 23 M21 32 L29 32" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M33 14 L33 32 M33 14 L41 14 M33 23 L40 23" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <circle cx="41" cy="11" r="3" fill="#E8B400" />
                  </svg>
                </div>
                <p className="text-white font-black text-sm text-center">MEF<br /><span className="text-slate-400 font-normal text-xs">Nigeria 🇳🇬</span></p>
              </div>
              {/* X connector */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-0.5 hidden sm:block" style={{ background: 'linear-gradient(90deg,rgba(196,30,58,0.3),rgba(196,30,58,0.8),rgba(196,30,58,0.3))' }} />
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-black" style={{ background: 'rgba(196,30,58,0.2)', border: '1px solid rgba(196,30,58,0.5)', color: '#fca5a5' }}>×</div>
                <div className="w-12 h-0.5 hidden sm:block" style={{ background: 'linear-gradient(90deg,rgba(196,30,58,0.3),rgba(196,30,58,0.8),rgba(196,30,58,0.3))' }} />
              </div>
              {/* TANG side */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: '#8B1A1A', border: '1px solid rgba(196,30,58,0.6)' }}>
                  {/* TANG logo approximation */}
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="32" cy="28" rx="26" ry="24" fill="#A01020" />
                    <ellipse cx="32" cy="30" rx="22" ry="20" fill="#8B1A1A" />
                    <text x="32" y="28" textAnchor="middle" fill="white" fontSize="14" fontFamily="Georgia, serif" fontStyle="italic" fontWeight="bold">tang</text>
                    <text x="32" y="40" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="5.5" fontFamily="Arial, sans-serif" letterSpacing="0.5">Chinese Education</text>
                    <text x="32" y="52" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="5" fontFamily="serif">唐风汉语</text>
                  </svg>
                </div>
                <p className="text-white font-black text-sm text-center">TANG<br /><span className="text-slate-400 font-normal text-xs">China 🇨🇳</span></p>
              </div>
            </div>

            {/* MOU details grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Left: About the partnership */}
              <div className="rounded-3xl p-7" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(196,30,58,0.2)' }}>
                <h3 className="text-white font-black text-lg mb-4 flex items-center gap-2">🐉 About TANG Chinese Education</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  <strong className="text-white">TANG Chinese Education & Technology Ltd.</strong> is headquartered at Jialong Building, Chaoyang District, Beijing, People's Republic of China. TANG International Education Group is a leading provider of Chinese language education, vocational skills training, and cloud-based learning platforms serving institutions worldwide.
                </p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Through its cloud platform (TANG Class APP), TANG delivers online live-streaming lessons, digital exam banks, auto-grading, real-time analytics, and cross-border curriculum resources — making it ideal for scaling Chinese education across Nigeria.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {['Beijing, China', '5-Year MOU', 'Signed Sep 9, 2025', 'Auto-renewable'].map(tag => (
                    <span key={tag} className="px-2.5 py-1 rounded-lg text-xs font-bold text-slate-300" style={{ background: 'rgba(196,30,58,0.12)', border: '1px solid rgba(196,30,58,0.25)' }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Right: Cooperation areas */}
              <div className="rounded-3xl p-7" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(196,30,58,0.2)' }}>
                <h3 className="text-white font-black text-lg mb-4">📋 Areas of Cooperation</h3>
                <div className="space-y-3">
                  {[
                    { icon: '🈵', title: 'Chinese Language Education', desc: 'Integration into Nigeria\'s education system — listening, reading, writing, speaking & Chinese culture' },
                    { icon: '🔧', title: 'Chinese + Vocational Skills', desc: 'AI, construction engineering, hospitality management & renewable energy — combined with language proficiency' },
                    { icon: '📜', title: 'Curriculum Co-development & Joint Certification', desc: 'Jointly issued certificates valid in Nigeria, China, and other regions' },
                    { icon: '🏭', title: 'Industry-Academia Collaboration', desc: 'Chinese enterprises in Nigeria providing skills programs and student job placement opportunities' },
                    { icon: '👩‍🏫', title: 'Teacher Ability Enhancement', desc: 'Training Nigerian educators to teach Chinese language programs independently' },
                  ].map(item => (
                    <div key={item.title} className="flex gap-3">
                      <span className="text-lg flex-shrink-0 mt-0.5">{item.icon}</span>
                      <div>
                        <p className="text-white text-sm font-bold leading-tight">{item.title}</p>
                        <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Programme formats */}
            <div className="rounded-3xl p-7 mb-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="text-white font-black text-lg mb-5 text-center">📅 Programme Framework</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { title: 'Chinese Language', duration: '3–6 months', enrol: '30–200 students', icon: '🈵' },
                  { title: 'Vocational Skills + Chinese', duration: '1–2 weeks / 3–6 months', enrol: '30–200 students', icon: '🔧' },
                  { title: 'Industry Collaboration', duration: 'Per industry agreement', enrol: 'Open enrolment', icon: '🏭' },
                ].map(prog => (
                  <div key={prog.title} className="rounded-2xl p-4 text-center" style={{ background: 'rgba(196,30,58,0.07)', border: '1px solid rgba(196,30,58,0.18)' }}>
                    <div className="text-3xl mb-2">{prog.icon}</div>
                    <p className="text-white font-black text-sm mb-1">{prog.title}</p>
                    <p className="text-slate-400 text-xs">⏱ {prog.duration}</p>
                    <p className="text-slate-500 text-xs">👥 {prog.enrol}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-slate-500 text-xs mt-4">Programs commence September 2025 · Delivery via TANG Cloud Platform + local physical sessions</p>
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-slate-400 text-sm mb-1">Interested in enrolling in Chinese language or vocational skills programmes?</p>
              <a href="mailto:info@muyikaratu.com" className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl font-black text-white text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg,#C41E3A,#8B1A1A)', boxShadow: '0 4px 24px rgba(196,30,58,0.35)' }}>
                🐉 Enquire About TANG Programmes →
              </a>
            </div>
          </div>
        </section>

        {/* ══ PORTAL FEATURES GRID ════════════════════════════════════════════ */}
        <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#020c18,#0f172a)' }}>
          <StarPattern opacity={0.05} />
          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-white mb-3">What's Inside the Portal</h2>
              <p className="text-slate-400">Powerful tools for every member of your school community</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: '🎓', title: 'Student Portal', desc: 'Access results, timetable, fees, CBT exams, and AI tutor', color: '#3D9B3D', role: 'Students' },
                { icon: '👨‍🏫', title: 'Teacher Portal', desc: 'Manage grades, attendance, IQA submissions, and lesson planning', color: '#1B3870', role: 'Teachers' },
                { icon: '🏛️', title: 'Admin Dashboard', desc: 'Full school management, analytics, and finance control', color: '#2a4f99', role: 'Administrators' },
                { icon: '🤖', title: 'AI Tutor & CBT', desc: 'Personalised AI tutoring, smart exam prep, and computer-based testing', color: '#E8B400', role: 'Students & Teachers' },
                { icon: '💰', title: 'Finance System', desc: 'Invoice generation, fee collection, payroll, and expense management', color: '#ef4444', role: 'Finance Office' },
                { icon: '📊', title: 'AI Analytics', desc: 'Real-time insights, performance predictions, and smart reporting', color: '#ec4899', role: 'Leadership' },
              ].map((feat, i) => (
                <div key={i} className="group relative rounded-2xl overflow-hidden p-6 transition-all hover:scale-[1.03] hover:-translate-y-1 cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${feat.color}22` }}>
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-full pointer-events-none opacity-10" style={{ background: `radial-gradient(circle,${feat.color},transparent)`, filter: 'blur(20px)' }} />

                  <div className="text-4xl mb-4">{feat.icon}</div>
                  <h3 className="font-black text-white text-lg mb-2">{feat.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{feat.desc}</p>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${feat.color}18`, color: feat.color, border: `1px solid ${feat.color}33` }}>
                    For: {feat.role}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => setLoginOpen(true)}
                className="px-10 py-4 rounded-2xl font-black text-white text-lg transition-all hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg,#1B3870,#2a4f99)', boxShadow: '0 8px 32px rgba(27,56,112,0.4)' }}
              >
                Sign In to Access All Features →
              </button>
            </div>
          </div>
        </section>

        {!isWhiteLabeled && <TryDemo onLogin={onLogin} />}

        {/* ══ PARTNER WITH US ═════════════════════════════════════════════════ */}
        {!isWhiteLabeled && (
          <section id="partner" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#030d1f,#0b1530,#03111f)' }}>
            <StarPattern opacity={0.06} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 60%, rgba(27,56,112,0.25), transparent)' }} />

            <div className="max-w-5xl mx-auto px-4 relative z-10">
              {/* eyebrow */}
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-bold" style={{ background: 'rgba(27,56,112,0.2)', border: '1px solid rgba(27,56,112,0.45)', color: '#93c5fd' }}>
                  🤝 White-Label Partnership Programme
                </div>
                <h2 className="text-4xl font-black text-white mb-4">Bring MEF EduSuite to Your School</h2>
                <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                  Your institution gets a fully branded portal — your name, your domain, your students — powered by the same IQA-certified platform trusted by schools across Nigeria, Canada, and China.
                </p>
              </div>

              {/* Credential badges */}
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {[
                  { icon: '🇬🇧', label: 'Ofqual UK Certified', sub: 'IQA Professionals' },
                  { icon: '🏆', label: 'Global IQA Standard', sub: 'VACRS Framework' },
                  { icon: '🎓', label: 'International School Principals', sub: 'Endorsed Programme' },
                ].map(b => (
                  <div key={b.label} className="flex items-center gap-3 px-5 py-3 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <span className="text-2xl">{b.icon}</span>
                    <div>
                      <p className="text-white font-black text-sm">{b.label}</p>
                      <p className="text-slate-400 text-xs">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tier cards */}
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12">
                {[
                  ...([['ess', '#3D9B3D'], ['std', '#1B3870'], ['pre', '#7c3aed']] as const).map(([id, color]) => ({
                    label: PLANS[id].name, color, highlight: id === 'std', features: PLANS[id].summary,
                    price: ngn(perPrice(id, pricing.appliedRate)), usd: `$${PLANS[id].per.toFixed(2)}`, base: ngn(basePrice(id, pricing.appliedRate)),
                  })),
                  { label: 'Enterprise', color: '#b45309', highlight: false, features: PLANS.ent.summary, price: 'Contact us', usd: 'info@muyikaratu.com', base: 'Agreed prices and volumes' },
                ].map(tier => (
                  <div
                    key={tier.label}
                    className="relative rounded-3xl overflow-hidden p-6"
                    style={{
                      background: (tier as any).highlight ? `linear-gradient(135deg,${tier.color}18,${tier.color}08)` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${tier.color}${(tier as any).highlight ? '55' : '30'}`,
                    }}
                  >
                    {(tier as any).highlight && (
                      <div className="absolute top-3 right-3">
                        <span className="text-[10px] font-black px-2.5 py-1 rounded-full" style={{ background: tier.color, color: '#fff' }}>POPULAR</span>
                      </div>
                    )}
                    <h3 className="font-black text-white text-lg mb-1">{tier.label}</h3>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-black" style={{ color: tier.color }}>{tier.price}</span>
                      <span className="text-slate-400 text-sm">{tier.price === 'Contact us' ? tier.usd : `per student per term · ${tier.usd}`}</span>
                    </div>
                    <p className="-mt-3 mb-4 text-xs text-slate-500">{tier.price === 'Contact us' ? tier.base : `+ ${tier.base} base per term · plus VAT`}</p>
                    <ul className="space-y-1.5 mb-5">
                      {tier.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-xs text-slate-400">
                          <span style={{ color: tier.color }}>✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Annual discount note */}
              <div className="text-center mb-10">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold" style={{ background: 'rgba(61,155,61,0.12)', border: '1px solid rgba(61,155,61,0.3)', color: '#4ABF4A' }}>
                  🏷️ First 14 days free · {discountSummary(pricing)}
                </span>
              </div>

              {/* Main CTA */}
              <div className="text-center">
                <button
                  onClick={() => setPartnerApplyOpen(true)}
                  className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl font-black text-white text-lg transition-all hover:scale-105 active:scale-95"
                  style={{ background: 'linear-gradient(135deg,#1B3870,#254d9e)', boxShadow: '0 12px 40px rgba(27,56,112,0.55)' }}
                >
                  <span className="text-2xl">🏫</span>
                  Apply to Partner with MEF EduSuite
                </button>
                <p className="text-slate-500 text-sm mt-4">
                  2-business-day review · Stripe, Paystack &amp; Flutterwave accepted · Cancel anytime
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ══ TRUST & VERIFICATION STRIP ══════════════════════════════════════ */}
        <section className="relative py-12 overflow-hidden" style={{ background: 'linear-gradient(135deg,#020c18,#0a1628)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="max-w-5xl mx-auto px-4">
            <p className="text-center text-xs font-black tracking-[0.35em] uppercase mb-8" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Verified · Listed · Trusted Globally
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">

              {/* Benevity */}
              <a
                href="https://benevity.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all hover:scale-105"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                {/* Benevity "b" icon */}
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#0A4FA0" />
                  <path d="M10 7h6.5c3 0 5 1.5 5 4 0 1.4-.7 2.5-1.8 3.1 1.5.6 2.5 1.9 2.5 3.5 0 2.8-2.2 4.4-5.5 4.4H10V7z" fill="white" />
                  <rect x="13" y="10" width="3.2" height="3.5" rx="1.6" fill="#0A4FA0" />
                  <rect x="13" y="15" width="3.8" height="3.8" rx="1.9" fill="#0A4FA0" />
                </svg>
                <div>
                  <p className="text-white font-black text-sm leading-tight">Benevity</p>
                  <p className="text-xs font-semibold" style={{ color: '#60a5fa' }}>Listed & Verified</p>
                </div>
                {/* Tick */}
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#22c55e' }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </a>

              {/* Goodstack */}
              <a
                href="https://goodstack.org"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all hover:scale-105"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                {/* Goodstack icon — purple with bar-chart motif */}
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#6B21A8" />
                  <rect x="7" y="18" width="4" height="7" rx="1.5" fill="white" opacity="0.7" />
                  <rect x="14" y="13" width="4" height="12" rx="1.5" fill="white" opacity="0.9" />
                  <rect x="21" y="8" width="4" height="17" rx="1.5" fill="white" />
                  <path d="M9 14l5-5 5 3 5-6" stroke="#E9D5FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                <div>
                  <p className="text-white font-black text-sm leading-tight">Goodstack</p>
                  <p className="text-xs font-semibold" style={{ color: '#c084fc' }}>Verified Nonprofit</p>
                </div>
                {/* Tick */}
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#22c55e' }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </a>

              {/* CAC Nigeria */}
              <div
                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#155d27" />
                  {/* Nigerian green shield */}
                  <path d="M16 5L7 9v8c0 5 3.8 9 9 10 5.2-1 9-5 9-10V9z" fill="white" opacity="0.15" />
                  <path d="M16 5L7 9v8c0 5 3.8 9 9 10 5.2-1 9-5 9-10V9z" stroke="white" strokeWidth="1.2" fill="none" />
                  <path d="M12 16l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <p className="text-white font-black text-sm leading-tight">CAC Nigeria</p>
                  <p className="text-xs font-semibold text-slate-400">Reg. No. 181465</p>
                </div>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#22c55e' }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </div>

              {/* NGO Permit */}
              <div
                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#92400e" />
                  {/* Document icon */}
                  <rect x="8" y="6" width="14" height="20" rx="2" fill="white" opacity="0.15" />
                  <rect x="8" y="6" width="14" height="20" rx="2" stroke="white" strokeWidth="1.2" fill="none" />
                  <path d="M11 13h8M11 17h8M11 21h5" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="23" cy="23" r="5" fill="#f59e0b" />
                  <path d="M21 23l1.5 1.5L25 21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <p className="text-white font-black text-sm leading-tight">NGO Permit</p>
                  <p className="text-xs font-semibold text-slate-400">MBEP/NGO-IC/1895</p>
                </div>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#22c55e' }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </div>

            </div>

            {/* Benevity projects note */}
            <p className="text-center text-xs text-slate-600 mt-6">
              6 published projects on Benevity · Enabling corporate giving for global donors to support MEF initiatives
            </p>
          </div>
        </section>

        {/* ══ SECURITY ══════════════════════════════════════════════════════════ */}
        <section id="security" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg,#020c18,#0a1628)' }}>
          {/* Decorative glow blobs */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.08) 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(6,182,212,0.06)' }} />

          <div className="relative z-10 max-w-6xl mx-auto px-4">
            {/* Header with hero shield */}
            <div className="text-center mb-16">
              <div className="relative w-28 h-28 mx-auto mb-6">
                {/* Rotating dashed ring */}
                <div className="absolute inset-0 rounded-full animate-spin-slow" style={{ border: '1.5px dashed rgba(16,185,129,0.25)' }} />
                <div className="absolute inset-2 rounded-full animate-spin-reverse" style={{ border: '1px dashed rgba(6,182,212,0.2)' }} />
                {/* Glow behind shield */}
                <div className="absolute inset-3 rounded-full animate-glow-emerald opacity-40" />
                {/* Shield */}
                <svg viewBox="0 0 100 110" className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)] drop-shadow-[0_0_25px_rgba(16,185,129,0.5)]">
                  <defs>
                    <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="50%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                  <path d="M50 3 L92 18 V52 C92 80 74 98 50 107 C26 98 8 80 8 52 V18 Z" fill="url(#shieldGrad)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                  <path d="M32 54 L45 68 L70 38" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-xs font-black tracking-[0.35em] uppercase mb-3" style={{ color: '#10b981' }}>Security &amp; Trust</p>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Your School's Data, Locked Down</h2>
              <p className="text-slate-400 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                MEF EduSuite is built on Google Cloud infrastructure with layered protections at every level —
                from the database to the browser. Here's exactly how we keep student, financial, and school data safe.
              </p>
            </div>

            {/* Security pillars grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  color: '#0d9488',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <path d="M13 2 L22 5.5 V12 C22 18 18 22 13 24 C8 22 4 18 4 12 V5.5 Z" fill="white" opacity="0.15" />
                      <path d="M13 2 L22 5.5 V12 C22 18 18 22 13 24 C8 22 4 18 4 12 V5.5 Z" stroke="white" strokeWidth="1.3" fill="none" />
                      <rect x="9.5" y="12" width="7" height="6" rx="1" fill="none" stroke="white" strokeWidth="1.3" />
                      <path d="M11 12v-1.5a2 2 0 0 1 4 0V12" fill="none" stroke="white" strokeWidth="1.3" />
                    </svg>
                  ),
                  title: 'Multi-School Data Isolation',
                  what: 'Every school’s records live in their own protected space.',
                  why: 'Every database request is checked against the requesting user’s own school before any data is returned. Access is enforced in the application layer today, with database-level rules being hardened collection-by-collection so isolation holds at every layer.',
                },
                {
                  color: '#7c3aed',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <circle cx="9" cy="9" r="5.5" fill="none" stroke="white" strokeWidth="1.4" />
                      <circle cx="9" cy="9" r="2" fill="white" />
                      <path d="M13 13 L22 22 M17.5 17.5 L20.5 14.5 M20 20 L23 17" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  ),
                  title: 'Zero-Trust Secrets Management',
                  what: 'Private keys and payment credentials never enter the browser.',
                  why: 'API keys for Stripe, Paystack, and Flutterwave live in Google Cloud Secret Manager and are only ever touched by secure backend Cloud Functions — never bundled into the front-end code where a hacker could inspect and steal them.',
                },
                {
                  color: '#b45309',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <rect x="4" y="3" width="18" height="20" rx="2" fill="none" stroke="white" strokeWidth="1.3" />
                      <path d="M8 8h10M8 12h10" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
                      <circle cx="18" cy="18" r="5" fill="#b45309" stroke="white" strokeWidth="1.3" />
                      <path d="M16 18l1.4 1.4L20 16.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  title: 'Cryptographic Signature Webhooks',
                  what: 'Payment confirmations are mathematically verified.',
                  why: 'When a payment gateway notifies the portal that fees were paid, the backend verifies that message with a cryptographic signature (HMAC-SHA512) before marking anything as paid — so a spoofed request can never fake a payment.',
                },
                {
                  color: '#1d4ed8',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <path d="M13 3c-3 3-5.5 4-8 4v7c0 5 3.5 8.5 8 9.5 4.5-1 8-4.5 8-9.5V7c-2.5 0-5-1-8-4z" fill="none" stroke="white" strokeWidth="1.3" />
                      <circle cx="13" cy="12" r="3" fill="none" stroke="white" strokeWidth="1.3" />
                      <path d="M9 19c0-2.2 1.8-4 4-4s4 1.8 4 4" fill="none" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  ),
                  title: 'Battle-Tested Identity Management',
                  what: 'Industry-standard login and password protection.',
                  why: 'Authentication runs on Firebase Authentication, built by Google. Passwords are never stored in plaintext — they’re hashed with advanced algorithms like scrypt on Google’s own identity servers.',
                },
                {
                  color: '#0e7490',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <path d="M4 9l9-6 9 6v3H4z" fill="none" stroke="white" strokeWidth="1.3" strokeLinejoin="round" />
                      <rect x="4" y="12" width="18" height="10" rx="1.5" fill="none" stroke="white" strokeWidth="1.3" />
                      <rect x="10.5" y="15.5" width="5" height="4" rx="1" fill="none" stroke="white" strokeWidth="1.2" />
                      <path d="M11.5 15.5v-1.2a1.5 1.5 0 0 1 3 0v1.2" fill="none" stroke="white" strokeWidth="1.2" />
                    </svg>
                  ),
                  title: 'Strict Storage Access Control',
                  what: 'Student recordings, documents, and media can’t be tampered with.',
                  why: 'Cloud Storage rules ensure school logos, banners, and student submissions can only be uploaded or changed by verified, authenticated users with the correct permissions — not by anonymous visitors.',
                },
                {
                  color: '#15803d',
                  icon: (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <circle cx="13" cy="11" r="8.5" fill="none" stroke="white" strokeWidth="1.3" />
                      <path d="M4.5 11h17M13 2.5c2.2 2.2 3.5 5.3 3.5 8.5s-1.3 6.3-3.5 8.5c-2.2-2.2-3.5-5.3-3.5-8.5S10.8 4.7 13 2.5z" fill="none" stroke="white" strokeWidth="1" />
                      <rect x="9.5" y="18" width="7" height="6" rx="1" fill="#15803d" stroke="white" strokeWidth="1.3" />
                      <path d="M11 18v-1.3a2 2 0 0 1 4 0V18" fill="none" stroke="white" strokeWidth="1.2" />
                    </svg>
                  ),
                  title: 'Secure Transit Encryption',
                  what: 'Every byte is encrypted the moment it leaves your device.',
                  why: 'The platform runs strictly on HTTPS. Payment details, report card comments, and exam submissions are encrypted in transit with industry-standard TLS, shielding them from anyone intercepting the connection.',
                },
              ].map((pillar) => (
                <div
                  key={pillar.title}
                  className="group relative p-6 rounded-3xl transition-all hover:-translate-y-1"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ background: pillar.color, boxShadow: `0 8px 24px ${pillar.color}55` }}
                  >
                    {pillar.icon}
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{pillar.title}</h3>
                  <p className="text-xs font-semibold mb-2" style={{ color: pillar.color === '#b45309' ? '#f59e0b' : pillar.color }}>{pillar.what}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{pillar.why}</p>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <p className="text-center text-xs text-slate-600 mt-12">
              🔒 Built on Google Cloud (Firestore, Firebase Auth, Cloud Storage, Cloud Functions) · Security hardening is continuous, not a one-time checkbox
            </p>
          </div>
        </section>

        {/* ══ TEACH ON MEF ══════════════════════════════════════════════════════ */}
        <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(180deg,#020c18 0%,#050f1a 50%,#020c18 100%)' }}>
          {/* Decorative glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.07) 0%, transparent 70%)' }} />
          <StarPattern opacity={0.03} />
          <div className="max-w-5xl mx-auto px-4 relative z-10">

            {/* Eyebrow */}
            <div className="flex justify-center mb-5">
              <span className="px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981' }}>For Educators</span>
            </div>

            {/* Headline */}
            <h2 className="text-center text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Teach on MEF Edusuite.<br />
              <span style={{ background: 'linear-gradient(90deg,#10b981,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Make a difference.</span>
            </h2>
            <p className="text-center text-slate-400 text-lg mb-14 max-w-2xl mx-auto">
              Join a growing network of certified teachers and tutors — in schools, training centres, or fully online.
            </p>

            {/* 3-step process */}
            <div className="grid md:grid-cols-3 gap-6 mb-14">
              {[
                { n: '01', title: 'Create your profile', desc: 'Tell us about your qualifications, experience, and subjects you love to teach.' },
                { n: '02', title: 'Get reviewed', desc: 'Our team reviews your application within 3–5 business days and sets up your account.' },
                { n: '03', title: 'Start teaching', desc: 'Log in to your teacher dashboard and begin making an impact on students\' lives.' },
              ].map(step => (
                <div key={step.n} className="relative p-6 rounded-2xl flex flex-col gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="text-4xl font-black" style={{ color: 'rgba(16,185,129,0.15)' }}>{step.n}</div>
                  <h3 className="text-white font-bold text-lg leading-tight">{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>

            {/* Benefit cards */}
            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {[
                { icon: '🗓️', title: 'Set your availability', desc: 'Teach on your schedule — mornings, evenings, or weekends. Full flexibility.' },
                { icon: '🎓', title: 'Teach your way', desc: 'In-person, online, or hybrid. Use our tools to manage classes, assignments, and grades.' },
                { icon: '📈', title: 'Grow your career', desc: 'Access professional development resources and build your teaching portfolio.' },
              ].map(card => (
                <div key={card.title} className="p-5 rounded-2xl flex gap-4 items-start" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="text-2xl flex-shrink-0">{card.icon}</span>
                  <div>
                    <h4 className="text-white font-bold text-base mb-1">{card.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex justify-center">
              <button
                onClick={() => setTeacherApplyOpen(true)}
                className="group inline-flex items-center gap-3 px-10 py-4 rounded-2xl text-white font-black text-lg transition-all hover:scale-[1.03] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 0 40px rgba(16,185,129,0.3)' }}
              >
                Apply to Teach Now
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>

          </div>
        </section>

        {/* ══ FOOTER ══════════════════════════════════════════════════════════ */}
        <footer className="relative py-16 overflow-hidden" style={{ background: '#020c18', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <StarPattern opacity={0.04} />
          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-4 gap-10 mb-12">
              {/* Brand */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                    <rect width="44" height="44" rx="10" fill="#1B3870" />
                    <path d="M5 32 L5 14 L11 24 L17 14 L17 32" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M11 24 L17 14 L17 32" stroke="#4ABF4A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M21 14 L21 32 M21 14 L29 14 M21 23 L28 23 M21 32 L29 32" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <path d="M33 14 L33 32 M33 14 L41 14 M33 23 L40 23" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <circle cx="41" cy="11" r="3" fill="#E8B400" />
                  </svg>
                  <div>
                    <p className="font-black text-white text-lg">MEF EduSuite</p>
                    <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#3D9B3D' }}>Education · Empowerment · Growth</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mb-2">
                  Muyikaratu Educational Foundation — empowering schools and training institutions across Nigeria, Canada, and China with world-class EdTech: AI tutoring, live classrooms, CBT exams, and smart analytics, all in one platform.
                </p>
                <p className="text-slate-500 text-xs mb-3">CAC Reg. No. 181465 · NGO Permit MBEP/NGO-IC/1895/Vol.1/39</p>
                {/* Trust badges */}
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'rgba(10,79,160,0.2)', border: '1px solid rgba(10,79,160,0.4)', color: '#93c5fd' }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    Benevity Listed
                  </span>
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'rgba(107,33,168,0.2)', border: '1px solid rgba(107,33,168,0.4)', color: '#c084fc' }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7.5 8,3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    Goodstack Verified
                  </span>
                </div>
              </div>

              {/* Quick links */}
              <div>
                <h4 className="font-black text-white mb-4 uppercase tracking-wider text-sm">Quick Links</h4>
                <ul className="space-y-2">
                  {['Home', 'About Us', 'News', 'Schools', 'Sign In'].map(link => (
                    <li key={link}>
                      <button onClick={() => { if (link === 'Sign In') { setLoginOpen(true); } else if (link === 'Schools') { scrollTo('admissions'); } else scrollTo(link.toLowerCase().replace(' ', '')); }} className="text-slate-400 hover:text-white text-sm transition-colors">
                        → {link}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-black text-white mb-4 uppercase tracking-wider text-sm">Contact Us</h4>
                <ul className="space-y-3 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">📍</span>
                    <span>No. 1 Uganda Street, Malali Low-Cost, Kaduna, Nigeria</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span>📞</span>
                    <span>+234 (0) 909 222 0178</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span>✉️</span>
                    <span>info@muyikaratu.com</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span>🕐</span>
                    <span>Mon–Fri: 8:00 AM – 5:00 PM</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <p className="text-slate-500 text-sm">
                  &copy; {new Date().getFullYear()} {isWhiteLabeled ? (currentSchool?.name || 'School') : 'Muyikaratu Educational Foundation'}. All rights reserved.
                </p>
                {isWhiteLabeled && (
                  <p className="text-xs mt-1" style={{ color: 'rgba(74,191,74,0.6)' }}>
                    Powered by <span className="font-bold" style={{ color: '#4ABF4A' }}>MEF EduSuite</span> · meflms.muyikaratu.com
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setLoginOpen(true)} className="px-4 py-2 rounded-lg text-sm font-bold hover:text-white transition-all" style={{ color: '#4ABF4A' }}>
                  Staff Login
                </button>
                <button onClick={() => setLoginOpen(true)} className="px-4 py-2 rounded-lg text-sm font-bold hover:text-white transition-all" style={{ color: '#4ABF4A' }}>
                  Student Login
                </button>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
};

export default LandingPage;
